// Translations for Council app
import esLocale from "./locales/es";
import arLocale from "./locales/ar";
import frLocale from "./locales/fr";

export const translations = {
  en: {
    // Login page
    "login.title": "MedConcil",
    "login.subtitle": "AI second opinion before you act",
    "login.welcome": "Welcome back",
    "login.signInMessage": "Sign in to access your MedConcil workspace",
    "login.continueWithGoogle": "Continue with Google",
    "login.terms": "By signing in, you agree to our Terms of Service and Privacy Policy",
    "login.feature1.title": "Multi-Model Consensus",
    "login.feature1.desc": "Query GPT and Op simultaneously",
    "login.feature2.title": "Enterprise PII Protection",
    "login.feature2.desc": "Automatic redaction of sensitive data before LLM processing",
    "login.feature3.title": "Team Collaboration",
    "login.feature3.desc": "Full audit logging and cost governance for your organization",
    "login.subtitle": "AI second opinion before you act",
    "login.welcome": "Welcome",
    "login.signInDesc": "Sign in to start a case",
    "login.signInGoogle": "Sign in with Google",
    "login.signingIn": "Signing in...",
    "login.signInEmail": "Sign in with Email",
    "login.password": "Password",
    "login.register": "Create account",
    "login.noAccount": "Don't have an account?",
    "login.createAccount": "Create one",
    "login.hasAccount": "Already have an account?",
    "login.signIn": "Sign in",
    "login.or": "or",
    "login.checkEmail": "Check your email for the confirmation link",
    "login.error": "Sign in failed",
    "login.oauthNotConfigured": "This sign-in method is not available in this deployment.",
    "login.forgotPassword": "Forgot password?",
    "login.rememberMe": "Remember me",
    "login.emailVerified": "Email verified. Please sign in.",
    "login.resetSent": "Password reset link sent to your email",
    "login.enterEmailFirst": "Enter your email first",
    "login.gdpr": "GDPR Compliant",
    "login.pii": "PII Redacted",
    "login.footer": "Informational second opinion — not a diagnosis or emergency care",

    // Legal
    "legal.termsNoticeSignin": "By signing in, you agree to our",
    "legal.termsNoticeSignup": "By creating an account, you agree to our",
    "legal.privacyPolicy": "Privacy Policy",
    "legal.termsOfUse": "Terms of Use",
    "legal.cookiePolicy": "Cookie Policy",
    "legal.and": "and",
    "legal.redirecting": "Redirecting to the legal page…",
    
    // Landing page
    "landing.eyebrow": "Second opinion, not a diagnosis",
    "landing.heroTitle": "When you need a second opinion before you act",
    "landing.heroSubtitle": "Multiple medical AIs review your case together. You get consensus, disagreements, and questions to bring to your doctor.",
    "landing.cta": "Start your case",
    "landing.ctaSecondary": "Sign in",
    "landing.signIn": "Sign in",
    "landing.howItWorks": "How it works",
    "landing.step1Title": "Describe your case",
    "landing.step1Desc": "Share symptoms, history, or questions in plain language. Add lab or imaging files if you have them.",
    "landing.step2Title": "AIs deliberate together",
    "landing.step2Desc": "Several medical models analyze your case independently, then a chairman synthesizes a transparent brief.",
    "landing.step3Title": "Take it to your doctor",
    "landing.step3Desc": "Download a PDF with consensus, uncertainties, and suggested questions — decision support, not a replacement for care.",
    "landing.notTitle": "What this is not",
    "landing.notDesc": "Not a lifelong health record, wearables hub, always-on chat companion, telehealth visit, or emergency triage service.",
    "landing.disclaimer": "For information only. Not a diagnosis. If you have urgent symptoms, seek emergency care.",
    "landing.footer": "Always discuss results with a qualified clinician.",

    // Sidebar & Navigation
    "nav.voice": "Voice",
    "nav.query": "Query",
    "nav.newCase": "New case",
    "nav.myCases": "Medical history",
    "nav.history": "History",
    "nav.admin": "Admin",
    "nav.adminSection": "Admin",
    "nav.clinicalSupport": "Clinical Decision Support",
    "nav.secondOpinion": "Second opinion",
    "nav.account": "Account",
    "nav.settings": "Settings",
    "nav.signOut": "Sign out",
    "nav.activeCouncil": "Active Council",
    "nav.plan": "plan",
    "nav.healthProfile": "Health profile",

    // Care-circle subjects
    "subjects.me": "Me",
    "subjects.addPerson": "Add person",
    "subjects.managePeople": "Manage people",
    "subjects.manageDesc": "People whose health you manage in this account.",
    "subjects.caseFor": "Case for:",
    "subjects.someoneElse": "Someone else (one-off, nothing saved)",
    "subjects.oneOffHint": "This case won't be saved to anyone's health profile.",
    "subjects.addAgeSex": "Add {name}'s age and sex for better answers",
    "subjects.useMyProfile": "Use my health profile ({summary})",
    "subjects.useNamedProfile": "Use {name}'s health profile ({summary})",
    "subjects.editProfile": "Edit profile",
    "subjects.ageYears": "{age} {yearsLabel} old",
    "subjects.ageMonths": "{age} {monthsLabel} old",
    "subjects.notProvided": "Not provided",
    "subjects.relationship": "Relationship",
    "subjects.displayName": "Name or nickname",
    "subjects.displayNamePlaceholder": "e.g. Grandma Anna",
    "subjects.birthMonth": "Birth month",
    "subjects.birthMonthRequired": "Birth month is required for children under 2",
    "subjects.consentTitle": "Consent attestation",
    "subjects.consentHint": "Confirm your legal basis for managing this person's health data.",
    "subjects.consent.guardian": "I am a parent or legal guardian of a minor",
    "subjects.consent.representative": "I am a legal representative (power of attorney / guardianship)",
    "subjects.consent.subject_consent": "This adult has agreed that I may manage their health data",
    "subjects.consentReadonly": "Consent: {basis} · attested {date}",
    "subjects.addValidation": "Name and consent attestation are required",
    "subjects.created": "Person added",
    "subjects.safetyStepTitle": "Optional: medications and allergies",
    "subjects.safetyStepHint": "You can skip this and add it later.",
    "subjects.safetyDone": "Done",
    "subjects.createError": "Could not add person",
    "subjects.limitReached": "You have reached the maximum number of people on this account.",
    "subjects.rel.self": "Me",
    "subjects.rel.child": "Child",
    "subjects.rel.parent": "Parent",
    "subjects.rel.grandparent": "Grandparent",
    "subjects.rel.partner": "Partner",
    "subjects.rel.sibling": "Sibling",
    "subjects.rel.other_family": "Other family",
    "subjects.rel.other": "Other",
    "subjects.settings": "Settings",
    "subjects.avatarColor": "Avatar color",
    "subjects.color.teal": "Teal",
    "subjects.color.amber": "Amber",
    "subjects.color.rose": "Rose",
    "subjects.color.indigo": "Indigo",
    "subjects.color.emerald": "Emerald",
    "subjects.color.sky": "Sky",
    "subjects.color.violet": "Violet",
    "subjects.color.orange": "Orange",
    "subjects.saved": "Saved",
    "subjects.saveError": "Could not save",
    "subjects.loadError": "Could not load person",
    "subjects.deletePerson": "Delete person",
    "subjects.deleteWarning": "Deleting a person also deletes all of their cases. Export first if you need a copy.",
    "subjects.deleteConfirm": "Delete this person and all of their cases? This cannot be undone.",
    "subjects.deleted": "Person deleted",
    "subjects.deleteError": "Could not delete person",
    "subjects.selfClearHint": "To clear your own health profile, use Account.",
    "subjects.placeholderFor": "Describe {name}'s symptoms or question…",
    "subjects.filterAll": "All people",
    "subjects.filterOneOff": "One-off cases",

    // Voice page
    "voice.pageTitle": "Voice Recording",
    "voice.readyTitle": "Record an appointment",
    "voice.readyDesc": "Press to start recording a conversation with the patient",
    "voice.consent": "Make sure the patient has given consent to recording",
    "voice.recordingLabel": "Recording in progress",
    "voice.transcribing": "Recognizing speech...",
    "voice.reviewLabel": "Conversation transcript — edit if needed",
    "voice.send": "Send to Consilium",
    "voice.reRecord": "Record again",
    "voice.noKey": "Groq API key not set",
    "voice.noKeyTitle": "Groq API key not set",
    "voice.noKeyDesc": "Add the key in the Admin section for speech recognition to work",
    "voice.goToAdmin": "Go to Admin",
    "voice.permDenied": "Microphone access denied. Check browser permissions.",
    "voice.noSupport": "Browser does not support audio recording",
    "admin.groqKeyLabel": "Groq API Key (for speech recognition)",
    "admin.groqKeyDesc": "Key for speech recognition via Groq Whisper. Stored locally in browser.",
    "admin.groqPersonalLabel": "Personal key (this browser only)",
    "admin.groqServerLabel": "Default key (for all users)",
    "admin.groqServerActive": "Active for all users",
    "admin.groqServerKeySet": "Server key is configured",
    "admin.groqServerKeyNotSet": "No server key set",
    "admin.groqServerSaved": "Server key saved",
    "admin.groqServerError": "Failed to save server key",
    "admin.groqKeySaved": "Groq API key saved",
    "admin.groqKeyCleared": "Groq API key cleared",
    "admin.saved": "Saved",
    "admin.save": "Save",

    // Query page
    "query.voiceCardTitle": "Record an appointment",
    "query.voiceCardDesc": "Record a conversation with the patient and send it to the Consilium",
    "query.chairmanStreaming": "Chairman is composing the response...",
    "query.clinicalSummary": "Clinical summary",
    "query.clinicalSummaryGenerating": "Clinical summary (generating…)",
    "query.title": "New case",
    "query.placeholder": "Describe your situation, symptoms, or question. Optional: attach labs or imaging.",
    "query.piiNotice": "Sensitive details are redacted before AI review",
    "query.submit": "Ask the council",
    "scenario.groupLabel": "Quick analysis",
    "scenario.caption": "Have a document? Choose what you need, and the council will analyze it.",
    "scenario.askWithoutFile": "Ask without a file",
    "scenario.attach.takePhoto": "Take a photo",
    "scenario.attach.choosePhotos": "Choose photos",
    "scenario.attach.chooseFiles": "Choose files",
    "scenario.attach.camera": "Camera",
    "scenario.summaryLabel": "Quick analysis: {name}",
    "scenario.lab_results.chip": "Explain labs",
    "scenario.lab_results.name": "Understand lab results",
    "scenario.lab_results.run": "Explain lab results",
    "scenario.lab_results.description": "Explains every out-of-range value and what to do next.",
    "scenario.lab_results.attach": "Attach your lab report (PDF or photo).",
    "scenario.imaging.chip": "Explain imaging",
    "scenario.imaging.name": "Explain an imaging report",
    "scenario.imaging.run": "Explain the imaging",
    "scenario.imaging.description": "Explains the findings and the conclusion in plain language.",
    "scenario.imaging.attach": "Attach the report or the images.",
    "scenario.treatment_plan.chip": "Check treatment",
    "scenario.treatment_plan.name": "Review a treatment plan",
    "scenario.treatment_plan.run": "Check the treatment plan",
    "scenario.treatment_plan.description": "Checks the prescribed treatment against the diagnosis and health profile.",
    "scenario.treatment_plan.attach": "Attach the prescription or discharge summary.",
    "scenario.diagnosis.chip": "Check diagnosis",
    "scenario.diagnosis.name": "Second opinion on a diagnosis",
    "scenario.diagnosis.run": "Check the diagnosis",
    "scenario.diagnosis.description": "Says what supports the diagnosis, what doesn't fit, and what to discuss with the doctor.",
    "scenario.diagnosis.attach": "Attach the doctor's report or test results.",
    "scenario.medications.chip": "Check medications",
    "scenario.medications.name": "Check medications",
    "scenario.medications.run": "Check the medications",
    "scenario.medications.description": "Checks interactions, doses, and side effects against the health profile.",
    "scenario.medications.attach": "Attach photos of the packs or the prescription.",
    "query.loading": "Reviewing your case...",
    "query.consiliumWorking": "Medical AIs are reviewing your case...",
    "query.remaining": "remaining",
    "query.almostDone": "Almost done...",
    "query.attachmentWaitNote": "Attached documents take longer — each advisor reads the full file. Please stay on this page.",
    "query.stagePii": "PII screening",
    "query.stageModels": "Models analyzing",
    "query.stageChairman": "Chairman synthesizing",
    "query.stageSaving": "Finalizing",
    "query.success": "Consilium has responded!",
    "query.stage1": "Individual Responses",
    "query.stage3": "Consilium Synthesis",
    "query.chairmanNote": "The Consilium reviewed all responses and compiled this final answer",
    "query.patientMessage": "Patient Message",
    "query.readyToSend": "Ready to review",
    "query.patientMessageNote": "Review and send to the patient. Written in plain language.",
    "query.physicianReview": "Physician Review Summary",
    "query.internalOnly": "Internal",
    "query.b2cSummary": "Summary",
    "query.b2cNextSteps": "Your next steps are",
    "query.b2cConsensus": "Where the models agree",
    "query.b2cDisagreements": "Differences & uncertainty",
    "query.b2cQuestions": "Questions for your doctor",
    "query.b2cRedFlags": "When to seek care",
    "query.b2cResultTitle": "Your second opinion",
    "query.b2cResultNote": "Multiple medical AIs selected to review your case:",
    "query.b2cFullResponse": "Full response",
    "query.b2cFullResponseNote": "We couldn't split this into sections, so the full response is shown below.",
    "query.b2cDisclaimerTitle": "Important",
    "query.b2cDisclaimer": "This is a second opinion generated by artificial intelligence, for information only. It is not a diagnosis, not emergency care, and not a replacement for a doctor. If you have urgent symptoms, seek emergency care. Always discuss these results with a qualified clinician.",
    "query.b2cDownloadReport": "Download report",
    "query.b2cCopyReport": "Copy report",
    "query.tokens": "Tokens",
    "query.yourCost": "Your Cost",
    "query.piiRedacted": "PII entities redacted",
    "query.models": "models",
    "query.emptyTitle": "Start a new case",
    "query.emptyDesc": "Describe what you want a second opinion on. Several medical AIs will review it and summarize consensus, disagreements, and questions for your doctor.",
    "query.disclaimerAcknowledgment": "I understand that this is a second opinion generated by artificial intelligence; it is not a diagnosis, emergency medical care, or a substitute for a doctor's consultation. I will discuss the results with a qualified professional.",
    "query.disclaimerNotice": "This is a second opinion generated by artificial intelligence. It is not a diagnosis, emergency medical care, or a substitute for a doctor's consultation. Discuss the results with a qualified professional.",
    "query.perspectiveLabel": "AI {specialty} advisor",
    "query.perspectiveFallback": "AI advisor",
    "query.perspectiveMarker": "AI",
    "query.perspectiveRow": "AI perspectives",
    "query.disclaimerRequired": "Accept the current clinical notice before submitting a case.",
    "query.councilForThisQuery": "Council for this query",
    "query.councilSelectedCount": "{count} selected",
    "query.specialistsSelected": "{count} specialists selected",
    "query.jumpToSection": "Jump to section",
    "query.metadata": "Metadata",
    "query.copyClinicalSummary": "Copy clinical summary",
    "query.copyPatientMessage": "Copy patient message",
    "query.pdfPhysician": "PDF (physician)",
    "query.pdfPatient": "PDF (patient)",
    "query.councilEmptyUsesDefault": "Doctors will be selected for your case.",
    "query.councilAutomatic": "Doctors will be selected for your case.",
    "query.councilGroupGeneralist": "Generalists",
    "query.councilGroupMedical": "Medical specialties",
    "query.councilGroupSurgical": "Surgical specialties",
    "query.councilGroupDiagnostic": "Diagnostic",
    "query.councilSearchPlaceholder": "Search by name or specialty",
    "query.councilClearAutomatic": "Clear (automatic)",
    "query.councilSelectedForCase": "Selected for your case",
    "query.councilExpandSpecialists": "Choose specialists",
    "query.councilCollapseSpecialists": "Hide specialists",
    "query.councilSelectionUpdated": "Council selection updated — some specialists are no longer available.",
    "query.freeCasesBanner": "Free cases remaining: {remaining} of {limit}",
    "query.trialDaysLeft": "Trial: {days} {daysLabel} left",
    "query.trialCasesLeft": "{remaining} {casesLabel} left today",
    "query.trialLimitReached": "Today's case limit is reached",
    "query.trialCaseUsed": "Today's trial case is used",
    "query.increaseLimit": "Increase limit",
    "unit.day.one": "day",
    "unit.day.few": "days",
    "unit.day.many": "days",
    "unit.case.one": "case",
    "unit.case.few": "cases",
    "unit.case.many": "cases",
    "unit.year.one": "year",
    "unit.year.few": "years",
    "unit.year.many": "years",
    "unit.month.one": "month",
    "unit.month.few": "months",
    "unit.month.many": "months",
    "unit.medication.one": "medication",
    "unit.medication.few": "medications",
    "unit.medication.many": "medications",
    "unit.allergy.one": "allergy",
    "unit.allergy.few": "allergies",
    "unit.allergy.many": "allergies",
    "query.onboardingBonusCta": "Complete Health Profile — unlock 2 extra cases",
    "query.postTrialPaywallHint": "Your trial has ended. Purchase a case to continue.",
    "query.trialDailyLimit": "Daily trial limit reached, try again tomorrow.",
    "query.trialExpired": "Your trial has ended — purchase a case to continue.",
    "query.rateLimit": "Rate limit exceeded",
    "query.addImage": "Photo",
    "query.addFile": "File",
    "query.fileTooLarge": "File too large (max 25MB)",
    "query.tooManyFiles": "Too many files (max {max}).",
    "query.attachmentsTooLarge": "Total attachments too large (max 50MB).",
    "query.unsupportedFile": "Unsupported file type",
    "query.imageTooLarge": "Image too large (max 10MB)",
    "query.timeout": "Query timed out. Please try again.",
    "query.followupTitle": "Follow-up questions to the council",
    "query.followupAnswer": "Follow-up answer",
    "query.followupPlaceholder": "Ask a follow-up question...",
    "query.followupError": "Error sending question",
    "query.followupLimitExceeded": "Follow-up limit reached for this case.",
    "query.followupLimitReached": "You've used all follow-up questions for this case. Start a new case to continue.",
    "query.followupStartNewCase": "Start a new case",
    "query.followupAttachments": "Attachments",
    "query.followupAttachmentsExhausted": "Follow-up attachment limit reached for this case.",
    "query.copied": "Copied",
    "query.copy": "Copy",
    "query.downloadPdf": "Download PDF",
    "query.formatPdf": "PDF",
    "query.formatDocx": "DOCX",
    "query.pdfDownloaded": "PDF downloaded",
    "query.pdfError": "Error downloading PDF",
    "query.pdfNoQuery": "No data to download — run a query first",
    "query.feedbackPrompt": "Was this helpful?",
    "query.feedbackHelpful": "Helpful",
    "query.feedbackNotHelpful": "Not helpful",
    "query.feedbackThanks": "Thanks for your feedback",
    "query.showRationale": "Show rationale",
    "query.rationaleComingSoon": "Detailed evidence linking coming soon",
    "query.textSize": "Text size",
    "query.textSizeDecrease": "Decrease text size",
    "query.textSizeIncrease": "Increase text size",
    "query.patientExportAttestationTitle": "Confirm patient export",
    "query.patientExportAttestation": "I have reviewed and adapted this message for my patient.",
    "query.patientExportConfirm": "Confirm and download",
    "query.docxPhysician": "Download physician DOCX",
    "query.docxPatient": "Download patient DOCX",
    "query.evidence.strong": "Strong evidence",
    "query.evidence.moderate": "Moderate evidence",
    "query.evidence.limited": "Limited evidence",
    "query.evidence.consensus": "Consensus",
    
    // Theme
    "theme.light": "Light",
    "theme.dark": "Dark",
    "theme.switchToLight": "Switch to light mode",
    "theme.switchToDark": "Switch to dark mode",
    "theme.clinicalAppearance": "Clinical appearance",
    "theme.clinicalAppearanceDesc": "Light or dark theme for the clinical workspace (login page unchanged)",
    
    // History page
    "history.title": "Medical history",
    "history.queries": "queries",
    "history.exportCsv": "Export CSV",
    "history.searchPlaceholder": "Search queries...",
    "history.startDate": "Start date",
    "history.endDate": "End date",
    "history.start": "Start",
    "history.end": "End",
    "history.clear": "Clear",
    "history.noQueries": "No queries yet",
    "history.timestamp": "Timestamp",
    "history.queryCol": "Query",
    "history.modelsCol": "Models",
    "history.costCol": "Your Cost",
    "history.piiCol": "PII",
    "history.view": "View",
    "history.queryDetails": "Case Details",
    "history.caseSummary": "Case summary",
    "history.exported": "History exported!",
    
    // Admin page
    "admin.title": "Admin Panel",
    "admin.dashboard": "Dashboard",
    "admin.auditLog": "Audit Log",
    "admin.team": "Team",
    "admin.settings": "Settings",
    "admin.accessDenied": "Access Denied",
    "admin.noPermission": "You don't have permission to access this page.",
    
    // Dashboard
    "dashboard.queriesThisMonth": "Queries this month",
    "dashboard.totalSpend": "Total spend",
    "dashboard.activeUsers": "Active users",
    "dashboard.avgCostPerQuery": "Avg cost/query",
    "dashboard.queriesPerDay": "Queries per Day",
    "dashboard.spendByModel": "Spend by Model",
    "dashboard.topUsers": "Top Users",
    "dashboard.user": "User",
    "dashboard.queries": "Queries",
    "dashboard.spend": "Spend",
    "dashboard.noData": "No data yet",
    
    // Team
    "team.title": "Team Members",
    "team.members": "members",
    "team.inviteMembers": "Invite Members",
    "team.inviteTitle": "Invite Team Members",
    "team.inviteDesc": "Add new members to your organization",
    "team.emailAddresses": "Email Addresses",
    "team.emailPlaceholder": "john@example.com, jane@example.com",
    "team.role": "Role",
    "team.member": "Member",
    "team.adminRole": "Admin",
    "team.auditor": "Auditor",
    "team.owner": "Owner",
    "team.cancel": "Cancel",
    "team.sendInvites": "Send Invites",
    "team.joined": "Joined",
    "team.actions": "Actions",
    "team.pendingInvites": "Pending Invites",
    
    // Settings
    "settings.councilConfig": "Consilium Configuration",
    "settings.councilConfigDesc": "Configure the AI specialists that participate in the consilium",
    "settings.councilMembers": "Consilium Members",
    "settings.chairmanModel": "Chairman Model",
    "settings.personasTitle": "Specialists",
    "settings.personasDesc": "Enable specialists, choose their backing model, and set the default council",
    "settings.personaSpecialty": "Specialty",
    "settings.personaBackingModel": "Backing model",
    "settings.personaEnabled": "Enabled",
    "settings.defaultCouncilMember": "In default council",
    "settings.chairmanPersona": "Chairman",
    "settings.atLeastOneCouncilMember": "Select at least one enabled specialist for the default council",
    "settings.chairmanMustBeEnabled": "The chairman specialist must stay enabled",
    "settings.chairmanRequired": "A chairman specialist is required",
    "settings.personaModelRequired": "Each selected specialist must have a backing model",
    "settings.councilNotConfigured": "Council not configured",
    "settings.piiRedaction": "PII Redaction",
    "settings.piiRedactionDesc": "Automatically redact sensitive data",
    "settings.saveModelSettings": "Save Model Settings",
    "settings.budgetSettings": "Budget Settings",
    "settings.budgetSettingsDesc": "Control your organization's spending",
    "settings.monthlyLimit": "Monthly Limit (USD)",
    "settings.unlimitedPlaceholder": "Leave empty for unlimited",
    "settings.alertThreshold": "Alert Threshold",
    "settings.currentSpend": "Current spend",
    "settings.saveBudgetSettings": "Save Budget Settings",
    "settings.dangerZone": "Danger Zone",
    "settings.resetMonthlySpend": "Reset Monthly Spend",
    "settings.resetDesc": "Set current month spend to $0.00",
    "settings.reset": "Reset",
    "settings.resetConfirmTitle": "Reset Monthly Spend?",
    "settings.resetConfirmDesc": "This will set the current month spend counter to $0.00. This action cannot be undone.",
    "settings.saving": "Saving...",
    
    // Audit
    "audit.filterByEmail": "Filter by email...",
    "audit.startDate": "Start",
    "audit.endDate": "End",
    "audit.export": "Export",
    "audit.timestamp": "Timestamp",
    "audit.user": "User",
    "audit.query": "Query",
    "audit.models": "Models",
    "audit.cost": "Cost",
    "audit.noLogs": "No audit logs found",
    "audit.exported": "Audit logs exported!",
    "audit.loadError": "Failed to load audit logs",
    
    // Toast messages
    "toast.dashboardError": "Failed to load dashboard",
    "toast.teamError": "Failed to load team",
    "toast.invitesSent": "Invitations sent!",
    "toast.inviteError": "Failed to send invites",
    "toast.roleUpdated": "Role updated",
    "toast.roleError": "Failed to update role",
    "toast.userRemoved": "User removed",
    "toast.userRemoveError": "Failed to remove user",
    "toast.modelsSaved": "Model settings saved!",
    "toast.modelsError": "Failed to save settings",
    "toast.budgetSaved": "Budget settings saved!",
    "toast.budgetError": "Failed to save settings",
    "toast.budgetReset": "Budget spend reset!",
    "toast.budgetResetError": "Failed to reset budget",
    "toast.settingsError": "Failed to load settings",
    
    // Account
    "account.title": "Account",
    "account.profile": "Profile",
    "account.organization": "Organization",
    "account.billing": "Billing",
    "account.billingDesc": "Manage your subscription and billing",
    "account.apiKey": "API Key",
    "account.apiKeyDesc": "Use this key to access MedConcil programmatically",
    "account.regenerate": "Regenerate API Key",
    "account.regenerateConfirmTitle": "Regenerate API Key?",
    "account.regenerateConfirmDesc": "Your current API key will be invalidated immediately. Any applications using the old key will stop working.",
    "account.regenerating": "Regenerating...",
    "account.exampleUsage": "Example usage:",
    "account.upgrade": "Upgrade",
    "account.billingComingSoon": "Billing integration coming soon. Contact support to upgrade.",

    // Health profile (B2C)
    "healthProfile.tierATitle": "Basic health information",
    "healthProfile.tierADesc": "Sharing basic information helps the consilium tailor recommendations. This is not a diagnosis.",
    "healthProfile.tierARequired": "Birth year and sex at birth are required",
    "healthProfile.disclaimerRequired": "Please confirm the second-opinion notice to continue",
    "healthProfile.tierASaved": "Basic profile saved",
    "healthProfile.extendedSaved": "Health profile saved",
    "healthProfile.saveError": "Could not save health profile",
    "healthProfile.saveIncomplete": "Some health details could not be saved. Please try again.",
    "healthProfile.birthYear": "Birth year",
    "healthProfile.sexAtBirth": "Sex at birth",
    "healthProfile.genderIdentity": "Gender identity (optional)",
    "healthProfile.genderIdentityPlaceholder": "Optional",
    "healthProfile.pregnancyStatus": "Pregnancy status",
    "healthProfile.selectPlaceholder": "Select…",
    "healthProfile.continue": "Continue",
    "healthProfile.privacyNote": "Your answers are used only as context for your cases.",
    "healthProfile.privacyLink": "Privacy policy",
    "healthProfile.sex.female": "Female",
    "healthProfile.sex.male": "Male",
    "healthProfile.sex.intersex": "Intersex",
    "healthProfile.sex.prefer_not_to_say": "Prefer not to say",
    "healthProfile.pregnancy.not_applicable": "Not applicable",
    "healthProfile.pregnancy.yes": "Yes",
    "healthProfile.pregnancy.possibly": "Possibly",
    "healthProfile.pregnancy.prefer_not_to_say": "Prefer not to say",
    "healthProfile.extendedTitle": "Health profile",
    "healthProfile.units.label": "Units",
    "healthProfile.units.metric": "Metric",
    "healthProfile.units.imperial": "Imperial",
    "healthProfile.heightCm": "Height (cm)",
    "healthProfile.heightFt": "Height (ft)",
    "healthProfile.heightIn": "Height (in)",
    "healthProfile.weightKg": "Weight (kg)",
    "healthProfile.weightLb": "Weight (lb)",
    "healthProfile.smoking": "Smoking",
    "healthProfile.alcohol": "Alcohol",
    "healthProfile.chronicConditions": "Chronic conditions",
    "healthProfile.chronicConditionsOther": "Other conditions",
    "healthProfile.medications": "Medications",
    "healthProfile.allergies": "Allergies",
    "healthProfile.surgeries": "Recent surgeries / procedures",
    "healthProfile.activityLevel": "Activity level",
    "healthProfile.activityDays": "Active days per week",
    "healthProfile.sleepHours": "Typical sleep (hours)",
    "healthProfile.sleepRegular": "Sleep regularity",
    "healthProfile.notesOther": "Anything else",
    "healthProfile.listPlaceholder": "Comma-separated",
    "healthProfile.contextFor": "Health Profile:",
    "healthProfile.section.safety": "Safety",
    "healthProfile.section.history": "History",
    "healthProfile.section.lifestyle": "Lifestyle",
    "healthProfile.sectionDesc.safety": "Medications and allergies for this person.",
    "healthProfile.sectionDesc.history": "Conditions and recent procedures.",
    "healthProfile.sectionDesc.lifestyle": "Measurements and daily habits.",
    "healthProfile.question.medications": "Regular medications?",
    "healthProfile.question.allergies": "Allergies?",
    "healthProfile.question.chronic_conditions": "Chronic conditions?",
    "healthProfile.question.surgeries_recent": "Surgery or procedure in the last year?",
    "healthProfile.yes": "Yes",
    "healthProfile.no": "No",
    "healthProfile.skip": "Skip",
    "healthProfile.more": "More",
    "healthProfile.saveSection": "Save",
    "healthProfile.safetyCardPrompt": "Any medications or allergies for {name}?",
    "healthProfile.safetyCardHint": "We ask so medication recommendations stay accurate and do not harm you.",
    "healthProfile.saveAndContinue": "Save and continue",
    "healthProfile.skipForNow": "Skip for now",
    "healthProfile.includeInCase": "Include my health profile in this case",
    "healthProfile.completeTierALink": "Complete basic profile",
    "healthProfile.accountTitle": "Health profile",
    "healthProfile.accountDesc": "Edit demographics and the optional health profile used as case context.",
    "healthProfile.saveTierA": "Save basic info",
    "healthProfile.saveExtended": "Save health profile",
    "healthProfile.deleteDataNote": "You can clear stored health profile data at any time.",
    "healthProfile.clearProfile": "Clear health profile",
    "healthProfile.cleared": "Health profile cleared",
    "healthProfile.smoking.never": "Never",
    "healthProfile.smoking.former": "Former",
    "healthProfile.smoking.current": "Current",
    "healthProfile.alcohol.none": "None",
    "healthProfile.alcohol.occasional": "Occasional",
    "healthProfile.alcohol.regular": "Regular",
    "healthProfile.alcohol.prefer_not_to_say": "Prefer not to say",
    "healthProfile.activity.sedentary": "Sedentary",
    "healthProfile.activity.light": "Light",
    "healthProfile.activity.moderate": "Moderate",
    "healthProfile.activity.vigorous": "Vigorous",
    "healthProfile.sleep.regular": "Mostly regular",
    "healthProfile.sleep.irregular": "Irregular",
    "healthProfile.sleep.prefer_not_to_say": "Prefer not to say",

    "billing.paywallTitle": "Continue with a paid case",
    "billing.paywallDesc": "Your free case quota is used. Purchase a single case or subscribe for monthly allowance.",
    "billing.buyOneCase": "Buy one case",
    "billing.subscribeMonthly": "Monthly plan",
    "billing.getMoreCases": "Get more cases",
    "billing.viewPlans": "View plans",
    "billing.freeQuotaExhausted": "Free case quota used — purchase to continue.",
    "billing.subscriptionExhausted": "Monthly allowance used — renew or buy a single case.",
    "billing.paymentRequired": "Payment required to submit another case.",
    "billing.trialSummary": "Trial today: {remaining}/{limit} · ends {ends}",
    "billing.onboardingBonusRemaining": "Onboarding bonus cases left: {count}",
    "billing.onboardingBonusCta": "Complete Health Profile — unlock 2 extra cases",
    "billing.postTrialPaywallHint": "Trial ended — buy a case or subscribe to continue.",
    "billing.checkoutUnavailable": "Online checkout is not available yet. Contact support.",
    "billing.checkoutError": "Could not start checkout. Try again or contact support.",
    "billing.successTitle": "Payment received",
    "billing.successDesc": "Your entitlement balance will update shortly after payment is confirmed.",
    "billing.successBalance": "Available: {free} free · {paid} prepaid · {sub} subscription cases",
    "billing.backToCases": "Back to new case",
    "billing.cancelTitle": "Checkout canceled",
    "billing.cancelDesc": "No charge was made. You can try again when ready.",
    "billing.accountBalance": "Cases available — free: {free}, prepaid: {paid}, subscription: {sub}",
    "billing.currentPlan": "Plan",
    
    // Onboarding
    "onboarding.createOrg": "Create Organization",
    "onboarding.setupWorkspace": "Set up your workspace",
    "onboarding.configureCouncil": "Configure Consilium",
    "onboarding.selectModels": "Select AI models",
    "onboarding.setBudget": "Set Budget",
    "onboarding.controlSpending": "Control spending",
    "onboarding.inviteTeam": "Invite Team",
    "onboarding.addCollaborators": "Add collaborators",
    "onboarding.createOrgTitle": "Create your organization",
    "onboarding.createOrgDesc": "This will be your team's workspace in MedConcil",
    "onboarding.orgName": "Organization Name",
    "onboarding.orgNamePlaceholder": "Acme Corp",
    "onboarding.workspaceUrl": "Workspace URL",
    "onboarding.createOrgBtn": "Create Organization",
    "onboarding.creating": "Creating...",
    "onboarding.configureCouncilTitle": "Configure your Consilium",
    "onboarding.configureCouncilDesc": "Select which AI models will participate in consensus",
    "onboarding.chairmanNote": "This model synthesizes the final consensus answer",
    "onboarding.back": "Back",
    "onboarding.continue": "Continue",
    "onboarding.setBudgetTitle": "Set spending limits",
    "onboarding.setBudgetDesc": "Control your organization's monthly AI spend",
    "onboarding.budgetNote": "Set a monthly spending cap. Leave empty for no limit.",
    "onboarding.alertNote": "Get notified when spending reaches this percentage of your budget",
    "onboarding.skip": "Skip",
    "onboarding.inviteTeamTitle": "Invite your team",
    "onboarding.inviteTeamDesc": "Add teammates to collaborate on Consilium queries",
    "onboarding.separateEmails": "Separate multiple emails with commas",
    "onboarding.defaultRole": "Default Role",
    "onboarding.skipFinish": "Skip & Finish",
    "onboarding.completeSetup": "Complete Setup",
    "onboarding.settingUp": "Setting up...",
    
    // Common
    "common.loading": "Loading...",
    "common.error": "Error",
    "common.success": "Success",
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.close": "Close",
    "common.back": "Back",
    "common.confirm": "Confirm",
    "common.authError": "Session expired. Please log in again.",
    "common.language": "Language",
    "common.english": "English",
    "common.spanish": "Español",
    "common.arabic": "العربية",
    "common.french": "Français",
    "common.russian": "Русский",

    "login.displayNameOptional": "Display name (optional)",
    "login.signUpTitle": "Create account",
    "login.signUpWithEmail": "Sign up with Email",
    "login.signUpDesc": "Sign up to access the clinical council",

    "nav.patients": "Patients",
    "nav.platform": "Platform",
    "nav.newQuery": "New case",
    "sidebar.collapse": "Collapse sidebar",
    "sidebar.expand": "Expand sidebar",
    "sidebar.openMenu": "Open menu",
    "query.newQuery": "New Query",
    "query.filesOnly": "Analyze the attached documents and images.",
    "query.confirmNewQuery": "Query is still processing. Start a new one?",
    "query.confirmNewQueryForce": "Query is still processing. Are you sure you want to start a new one?",

    "reset.invalidLink": "Invalid Link",
    "reset.tokenMissing": "Reset token is missing.",
    "reset.backToLogin": "Back to Login",
    "reset.passwordUpdated": "Password Updated",
    "reset.canSignIn": "You can now sign in with your new password.",
    "reset.signIn": "Sign In",
    "reset.newPassword": "New Password",
    "reset.enterNewPassword": "Enter a new password for your account",
    "reset.confirmPassword": "Confirm password",
    "reset.savePassword": "Save Password",
    "reset.passwordMin": "Password must be at least 6 characters",
    "reset.passwordMismatch": "Passwords do not match",
    "reset.success": "Password updated successfully",
    "reset.linkInvalid": "Link is invalid or expired",
    "reset.failed": "Password reset failed",

    "patients.title": "Patient Cards",
    "patients.newPatient": "New Patient",
    "patients.searchPlaceholder": "Search by name...",
    "patients.noPatients": "No patients",
    "patients.name": "Name",
    "patients.birthDate": "Date of birth",
    "patients.gender": "Gender",
    "patients.genderMale": "M",
    "patients.genderFemale": "F",
    "patients.actions": "Actions",
    "patients.open": "Open",
    "patients.loadError": "Failed to load patient",
    "patients.nameRequired": "Name is required",
    "patients.saved": "Patient saved",
    "patients.saveError": "Failed to save",
    "patients.deleted": "Patient deleted",
    "patients.deleteError": "Failed to delete",
    "patients.back": "Back",
    "patients.edit": "Edit",
    "patients.delete": "Delete",
    "patients.newPatientTitle": "New Patient",
    "patients.editPatientTitle": "Edit Patient",
    "patients.save": "Save",
    "patients.saving": "Saving...",
    "patients.personalInfo": "Personal information",
    "patients.fullName": "Full name",
    "patients.phone": "Phone",
    "patients.email": "Email",
    "patients.medicalProfile": "Medical profile",
    "patients.bloodType": "Blood type",
    "patients.allergies": "Allergies",
    "patients.diagnoses": "Chronic diagnoses",
    "patients.medications": "Current medications",
    "patients.surgeries": "Surgical history",
    "patients.familyHistory": "Family history",
    "patients.notes": "Notes",
    "patients.documents": "Documents",
    "patients.sessions": "Consilium sessions",
    "patients.upload": "Upload",
    "patients.askConsilium": "Ask Consilium",
    "patients.deleteConfirmTitle": "Delete patient?",
    "patients.deleteConfirmDesc": "This action cannot be undone.",
    "patients.cancel": "Cancel",
    "patients.notFound": "Patient not found",
    "patients.genderMaleFull": "Male",
    "patients.genderFemaleFull": "Female",
    "patients.consilium": "Consilium",
    "patients.profile": "Profile",
    "patients.contact": "Contact",
    "patients.snils": "SNILS",
    "patients.snilsPlaceholder": "123-456-789 00",
    "patients.mrn": "MRN",
    "patients.mrnPlaceholder": "12345678",
    "patients.phonePlaceholderUs": "+1 (555) 555-5555",
    "patients.phonePlaceholderCis": "+7...",
    "patients.created": "Created",
    "patients.diagnosis": "Diagnosis",
    "patients.status": "Status",
    "patients.medication": "Medication",
    "patients.dose": "Dose",
    "patients.frequency": "Frequency",
    "patients.patientDocuments": "Patient Documents",
    "patients.titleCol": "Title",
    "patients.category": "Category",
    "patients.date": "Date",
    "patients.size": "Size",
    "patients.noDocuments": "No documents",
    "patients.mode": "Mode",
    "patients.docUploaded": "Document uploaded",
    "patients.uploadError": "Upload error",
    "patients.docDeleted": "Document deleted",
    "patients.add": "Add",
    "patients.searchNamePhone": "Search by name or phone...",
    "patients.search": "Search",
    "patients.birthDateShort": "Birth Date",
    "patients.genderShort": "Sex",
    "patients.noPatientsYet": "No patients yet",
    "patients.noSessionsYet": "No sessions yet",
    "patients.deleteCardTitle": "Delete patient card?",
    "patients.cardUpdated": "Card updated",
    "patients.patientCreated": "Patient created",
    "patients.editCard": "Edit Card",
    "patients.basicInfo": "Basic Info",
    "patients.genderOther": "Other",
    "patients.noAllergies": "No allergies",
    "patients.substance": "Substance",
    "patients.severity": "Severity",
    "patients.severityMild": "Mild",
    "patients.severityModerate": "Moderate",
    "patients.severitySevere": "Severe",
    "patients.reaction": "Reaction",
    "patients.noDiagnoses": "No diagnoses",
    "patients.nameLabel": "Name",
    "patients.icd10": "ICD-10",
    "patients.noMedications": "No medications",
    "patients.noSurgeries": "No surgeries",
    "patients.hospital": "Hospital",
    "patients.familyHistoryHint": "Family history (comma-separated)",
    "patients.additionalNotes": "Additional notes",
    "patients.statusActive": "Active",
    "patients.statusRemission": "Remission",
    "patients.statusResolved": "Resolved",
    "patients.surgery": "Surgery",
    "patients.familyNotes": "Family History & Notes",
    "patients.familyExample": "Diabetes (mother)",
    "patients.additionalNotesPlaceholder": "Additional notes...",

    // Onboarding
    "onboarding.stepOrg": "Create Organization",
    "onboarding.stepCouncil": "Configure Council",
    "onboarding.stepBudget": "Set Budget",
    "onboarding.stepTeam": "Invite Team",
    "onboarding.createOrg": "Create your organization",
    "onboarding.createOrgDesc": "This will be your team's workspace",
    "onboarding.orgName": "Organization Name",
    "onboarding.orgNamePlaceholder": "My Clinic",
    "onboarding.workspaceUrl": "Workspace URL",
    "onboarding.slugPlaceholder": "my-clinic",
    "onboarding.createOrgBtn": "Create Organization",
    "onboarding.creating": "Creating...",
    "onboarding.orgCreated": "Organization created!",
    "onboarding.fillFields": "Please fill in all fields",
    "onboarding.configCouncil": "Configure your Council",
    "onboarding.configCouncilDesc": "Select AI models for consensus",
    "onboarding.councilMembers": "Council Members",
    "onboarding.chairmanModel": "Chairman Model",
    "onboarding.chairmanModelDesc": "This model synthesizes the final consensus answer",
    "onboarding.continue": "Continue",
    "onboarding.setBudget": "Set spending limits",
    "onboarding.setBudgetDesc": "Control your monthly AI spend",
    "onboarding.monthlyBudget": "Monthly Budget",
    "onboarding.budgetPlaceholder": "Leave empty for unlimited",
    "onboarding.budgetHint": "Set a monthly spending cap. Leave empty for no limit.",
    "onboarding.alertThreshold": "Alert Threshold",
    "onboarding.alertHint": "Get notified when spending reaches this percentage",
    "onboarding.skip": "Skip",
    "onboarding.inviteTeam": "Invite your team",
    "onboarding.inviteTeamDesc": "Add teammates to collaborate",
    "onboarding.emailAddresses": "Email Addresses",
    "onboarding.emailsHint": "Separate multiple emails with commas",
    "onboarding.defaultRole": "Default Role",
    "onboarding.roleMember": "Member",
    "onboarding.roleAdmin": "Admin",
    "onboarding.roleAuditor": "Auditor",
    "onboarding.skipFinish": "Skip & Finish",
    "onboarding.completeSetup": "Complete Setup",
    "onboarding.settingUp": "Setting up...",
    "onboarding.setupComplete": "Setup complete!",
    "onboarding.startBtn": "Get Started",

    // Patient picker / confirm modals
    "patient.pickTitle": "Select patient",
    "patient.searchPlaceholder": "Name or phone...",
    "patient.notFound": "No patients found",
    "patient.registerNew": "Register new patient",
    "patient.newTitle": "New patient",
    "patient.fullName": "Full name",
    "patient.fullNamePlaceholder": "John Michael Smith",
    "patient.birthDate": "Date of birth",
    "patient.gender": "Sex",
    "patient.genderSelect": "Select",
    "patient.genderMale": "Male",
    "patient.genderFemale": "Female",
    "patient.genderOther": "Other",
    "patient.phone": "Phone",
    "patient.phonePlaceholderUs": "+1 (555) 555-5555",
    "patient.phonePlaceholderCis": "+7 916 123-45-67",
    "patient.email": "Email",
    "patient.emailPlaceholder": "patient@email.com",
    "patient.create": "Create",
    "patient.errNameMin": "At least 2 words (Last First)",
    "patient.errRequired": "Required field",
    "patient.errFutureDate": "Date is in the future",
    "patient.errInvalidDate": "Invalid date",
    "patient.errPhoneFormatUs": "Format: +1XXXXXXXXXX",
    "patient.errPhoneFormatCis": "Format: +7XXXXXXXXXX",
    "patient.created": "Patient registered",
    "patient.createError": "Failed to create patient",
    "patient.confirmTitle": "Create a query for",
    "patient.confirmYes": "Yes, create",
    "patient.confirmChange": "Choose another",

    // Query page (patient + downloads)
    "query.changePatient": "Change patient",
    "query.patientLabel": "Patient:",
    "query.docxDownloaded": "DOCX downloaded",
    "query.downloadError": "Download failed",
    "query.copyError": "Failed to copy",

    // Auth callback
    "auth.authorizing": "Authorizing...",
    "auth.authError": "Authorization error",

    // Account page (additional)
    "account.loadError": "Failed to load account",
    "account.keyRegenerated": "API key regenerated!",
    "account.keyRegenerateError": "Failed to regenerate key",
    "account.keyCopied": "API key copied!",
    "account.createdLabel": "Created",
    "account.orgId": "Organization ID",
    "account.signOut": "Sign Out",
    "account.cancel": "Cancel",
    "account.regenerateShort": "Regenerate",
    "account.planSuffix": "Plan",
    "account.planTeamDesc": "Up to 25 users, unlimited queries",
    "account.planBusinessDesc": "Up to 150 users, SSO available",
    "account.planEnterpriseDesc": "Unlimited users, custom features",
    "account.clinicProfile": "Clinic profile",
    "account.clinicProfileDesc": "Details for the header of exported documents (PDF/DOCX)",
    "account.clinicName": "Clinic name",
    "account.clinicNamePlaceholder": "City Clinic #1",
    "account.doctorName": "Doctor name",
    "account.doctorNamePlaceholder": "J. Smith, MD, therapist",
    "account.clinicSaved": "Clinic profile saved",
    "account.clinicSaveError": "Failed to save",
    "account.save": "Save",
    "account.deleteAccount": "Delete account",
    "account.deleteAccountTitle": "Delete your account?",
    "account.deleteAccountDesc": "This permanently deletes your account, health profile, cases, and attachments. This cannot be undone.",
    "account.deleteAccountPassword": "Enter your password to confirm",
    "account.deleteAccountPasswordPlaceholder": "Password",
    "account.deleteAccountConfirm": "Delete permanently",
    "account.deleteAccountSuccess": "Your account has been deleted",
    "account.deleteAccountError": "Could not delete account",
    "account.deleteAccountReauth": "Please sign in again, then retry within 5 minutes",

    // Platform dashboard
    "platform.title": "Platform",
    "platform.totalUsers": "Total users",
    "platform.inPeriod": "in period",
    "platform.queriesToday": "Queries today",
    "platform.spendPeriod": "Spend in period",
    "platform.perQuerySuffix": "query",
    "platform.errors": "Errors",
    "platform.registrations": "Registrations",
    "platform.registrationsBar": "Registrations",
    "platform.yandex": "Yandex",
    "platform.noData": "No data",
    "platform.topUsers": "Top users by spend",
    "platform.user": "User",
    "platform.clinic": "Clinic",
    "platform.owner": "Owner",
    "platform.queries": "Queries",
    "platform.spend": "Spend",
    "platform.spendByModel": "Spend by model",
    "platform.consultations": "Queries in period",
    "platform.consultationsBar": "Queries",
    "platform.clinicsTitle": "Clinics & entitlements",
    "platform.clinicsDesc": "Set partner, trial, paid, or expired plan per organization.",
    "platform.usersTitle": "Users & access",
    "platform.usersDesc": "Partner accounts have unlimited cases (no paywall). User follows normal free and paid limits.",
    "platform.accessTier": "Access",
    "platform.registered": "Registered",
    "platform.tier.user": "User",
    "platform.tier.partner": "Partner",
    "platform.tierSaved": "Access updated",
    "platform.tierError": "Failed to update access",
    "platform.plan": "Plan",
    "platform.actions": "Actions",
    "platform.savePlan": "Save",
    "platform.entitlementSaved": "Plan updated",
    "platform.entitlementError": "Failed to update plan",
    "platform.plan.trial": "Trial",
    "platform.plan.partner": "Partner",
    "platform.plan.paid": "Paid",
    "platform.plan.expired": "Expired",
    "period.today": "Today",
    "period.week": "7 days",
    "period.month": "30 days",
    "period.all": "All time",

    // History page (additional)
    "history.synthesis": "Summary",
    "history.forPatient": "For patient",
    "history.forDoctor": "For doctor",
    "history.pdfForPatient": "PDF for patient",
    "history.pdfForDoctor": "PDF for doctor",
    "history.docxDownloaded": "DOCX downloaded",
    "history.pdfDownloaded": "PDF downloaded",
    "history.downloadError": "Download failed",
    "history.addRecord": "Add record",
    "history.summary": "Summary",
    "history.timeline": "Timeline",
    "history.uploadDocument": "Upload document",
    "history.filesSelected": "{count} files selected",
    "history.tooManyFiles": "You can attach up to {max} files to one record.",
    "history.addEntry": "Add entry",
    "history.category": "Category",
    "history.eventDate": "Date of care",
    "history.datePrecision": "Date precision",
    "history.precision.day": "Day",
    "history.precision.month": "Month",
    "history.precision.year": "Year",
    "history.dateUnknown": "Date unknown",
    "history.titleField": "Title",
    "history.facility": "Facility or clinician",
    "history.notes": "Notes",
    "history.details": "Details",
    "history.save": "Save",
    "history.cancel": "Cancel",
    "history.delete": "Delete",
    "history.deleteConfirm": "Delete this record? This cannot be undone.",
    "history.deleteCaseDocuments.one": "Also delete the document saved from this case",
    "history.deleteCaseDocuments.few": "Also delete {n} documents saved from this case",
    "history.deleteCaseDocuments.many": "Also delete {n} documents saved from this case",
    "history.attachedFiles.one": "{n} attached file",
    "history.attachedFiles.few": "{n} attached files",
    "history.attachedFiles.many": "{n} attached files",
    "history.dateFromCase": "Date taken from the case. Change it if the file is older.",
    "history.fromCase": "From case: {title}",
    "history.fromCaseDated": "From a case of {date}",
    "history.fromCaseUntitled": "From a case",
    "history.fromCasePending": "From a case in progress",
    "history.fromCaseDeleted": "From a deleted case",
    "history.filePages": "{n} pages",
    "history.edit": "Edit",
    "history.download": "Download",
    "history.previewUnavailable": "Preview not available",
    "history.noRecords": "No records yet",
    "history.noOneOff": "No one-off cases",
    "history.aiSecondOpinion": "AI second opinion — not a medical record",
    "history.recordCount": "{count} records",
    "history.storageUsage": "{used} MB of {limit} MB used",
    "history.conditions": "Conditions",
    "history.medications": "Medications",
    "history.allergies": "Allergies",
    "history.noneReported": "None reported",
    "history.notProvided": "Not added yet",
    "history.review": "Review",
    "history.latest": "Most recent",
    "history.consentTitle": "Store health records",
    "history.consentAccept": "I agree",
    "history.consentRequired": "Accept storage consent before adding a record.",
    "history.duplicateWarning": "A file with the same contents is already in this history. Both copies were kept.",
    "history.storageFull": "Medical history storage is full. Remove a large file and try again.",
    "history.caseFilesNotSavedTitle": "Files were not saved",
    "history.caseFilesNotSaved": "These files were not saved to medical history. Add them later from Medical history.",
    "history.caseFilesNotSavedOk": "OK",
    "history.shareSessionExpired": "Your viewing session ended. Enter the access code again.",
    "history.saved": "Record saved",
    "history.saveError": "Could not save the record",
    "history.loadError": "Could not load medical history",
    "history.deleted": "Record deleted",
    "history.saveFilesToHistory": "Save these files to {name}'s medical history",
    "history.oneOffCases": "One-off cases",
    "history.tierA": "Age and sex at birth",
    "history.fileUnavailable": "File no longer available",
    "history.updated": "Updated {date}",
    "history.source.user_upload": "Uploaded by you",
    "history.source.user_entered": "Entered by you",
    "history.source.case_attachment": "Saved from a case",
    "history.source.medconcil_case": "MedConcil case",
    "history.category.visit_note": "Visit note",
    "history.category.discharge_summary": "Discharge summary",
    "history.category.lab_result": "Lab result",
    "history.category.imaging_report": "Imaging report",
    "history.category.prescription": "Prescription",
    "history.category.procedure": "Procedure",
    "history.category.immunization": "Immunization",
    "history.category.referral": "Referral",
    "history.category.other": "Other",
    "history.category.ai_second_opinion": "AI second opinion",
    "history.export": "Export",
    "history.exportTitle": "Export medical history",
    "history.exportDescription": "A clinical summary for a doctor. MedConcil does not verify these records.",
    "history.exportFormat": "Format",
    "history.format.pdf": "PDF summary",
    "history.format.fhir": "FHIR patient summary (JSON)",
    "history.format.zip": "ZIP with PDF, FHIR, and documents",
    "history.includeAiOpinions": "Include AI second opinions",
    "history.exportAiHint": "AI second opinions are left out unless this is checked.",
    "history.exportWholeHistory": "Whole history",
    "history.exportChooseRecords": "Choose records",
    "history.pickerTruncated": "Showing the newest {n} records. Older records in this period are included.",
    "history.exportIdentityTitle": "Legal name and date of birth",
    "history.exportIdentityHelp": "Clinics need these on a summary. Save them for next time, or use them once. Leave the name blank and the document says “Name not provided”.",
    "history.exportLegalName": "Legal name",
    "history.exportDateOfBirth": "Date of birth",
    "history.exportSaveIdentity": "Save for future exports",
    "history.exportUseSaved": "Use the saved name and date of birth",
    "history.exportChangeIdentity": "Enter a different name or date of birth",
    "history.exportDownload": "Download",
    "history.exportWorking": "Preparing export…",
    "history.exportDone": "Export downloaded",
    "history.exportError": "Could not export medical history",
    "history.exportTooLarge": "This export is too large. Turn off documents or select fewer records and try again.",
    "history.exportIdentityInvalid": "Enter a real date of birth, or leave it blank.",
    "history.share": "Share",
    "history.shareTitle": "Share with a doctor",
    "history.shareDescription": "A time-limited link. You can stop it. Copies already downloaded stay with the recipient.",
    "history.shareExpires": "Link expires",
    "history.shareExpires.1h": "1 hour",
    "history.shareExpires.24h": "24 hours",
    "history.shareExpires.7d": "7 days",
    "history.shareExpires.30d": "30 days",
    "history.shareRequireCode": "Require a 6-digit code",
    "history.shareNotify": "Email me when the link is first opened",
    "history.shareConsentAccept": "I agree to send these records",
    "history.shareCreate": "Create link",
    "history.shareWorking": "Creating link…",
    "history.shareCodeOnce": "Copy the link and code now. They cannot be shown again.",
    "history.shareUrl": "Link",
    "history.shareCode": "Access code",
    "history.shareCopy": "Copy link",
    "history.shareCopyCode": "Copy access code",
    "history.shareCopied": "Link copied",
    "history.shareCodeCopied": "Access code copied",
    "history.shareDone": "Done",
    "history.shareError": "Could not update sharing",
    "history.shareConsentRequired": "Accept the sharing consent before creating a link.",
    "history.shareLimit": "This person already has the maximum number of active links.",
    "history.shareWholeHistory": "Whole history",
    "history.shareChooseRecords": "Choose records",
    "history.shareIncludeSummary": "Include the summary",
    "history.sharePersonFallback": "this person",
    "history.sharesTitle": "Shared links",
    "history.noActiveShares": "No active links",
    "history.shareStop": "Stop sharing",
    "history.shareStopped": "Link stopped",
    "history.shareOpens": "Opened {count} times",
    "history.shareExpiresAt": "Expires {date}",
    "history.shareCreatedAt": "Created {date}",
    "history.shareScopeSummary": "Summary",
    "history.shareScopeRecords": "{count} records",
    "history.shareScopeAi": "Includes AI opinions",
    "history.downloadPacket": "Download",
    "history.shareWithDoctor": "Share with a doctor",
    "history.packetTitle": "Prepare for a doctor",
    "history.packetDescription": "Choose what this doctor should see, then download a file or create a link.",
    "history.packetStepInclude": "What to include",
    "history.packetStepSend": "How to send it",
    "history.scopeProfile": "Profile summary (allergies, medications, conditions, lifestyle)",
    "history.scopeIdentity": "Legal name and date of birth",
    "history.scopeDocuments": "Attach original documents",
    "history.shareDoctorNote": "Note for the doctor",
    "history.scopeNote": "Note for the doctor",
    "history.scopeNotePlaceholder": "Optional. Up to 1,000 characters.",
    "history.scopePeriodWhole": "Period: whole history",
    "history.scopePreview": "{records} records · {documents} documents ({size}) · {consultations} consultations",
    "history.scopePreviewLoading": "Counting records…",
    "history.packetSaveIdentity": "Saved for future packets",
    "history.packetSaveRequired": "A link that includes a name or date of birth saves them for future packets.",
    "history.packetIdentityOmitted": "This link will not include the name or date of birth.",
    "history.chooseOneItem": "Choose at least one item.",
    "history.profileAsOf": "Profile current as of {date}",
    "history.pregnancy": "Pregnancy",
    "history.activeProblems": "Active problems",
    "history.vitals": "Vitals and lifestyle",
    "history.height": "Height",
    "history.weight": "Weight",
    "history.bmi": "BMI",
    "history.smoking": "Smoking",
    "history.alcohol": "Alcohol",
    "history.activity": "Activity",
    "history.sleep": "Sleep",
    "history.surgeries": "Procedures and surgeries",
    "history.scopeError.empty": "Choose at least one item.",
    "history.scopeError.outside_period": "One or more records are outside the selected period.",
    "history.scopeError.not_found": "One or more records were not found.",
    "history.scopeError.invalid_period": "That period is not valid.",
    "history.scopeError.note_too_long": "The note for the doctor must be 1,000 characters or fewer.",
    "history.scopeError.ambiguous_shape": "Choose records in one way only.",
    "history.shareScopeProfile": "profile",
    "history.shareScopeIdentity": "identity",
    "history.shareScopeDocuments": "documents",
    "history.shareScopeAiShort": "AI",
    "history.shareScopePeriod.whole": "Whole history",
    "history.shareScopePeriod.12m": "Last 12 months",
    "history.shareScopePeriod.3y": "Last 3 years",
    "history.shareScopePeriod.5y": "Last 5 years",
    "history.shareScopePeriod.custom": "Custom period",
    "history.shareScopePeriodRange": "{label} ({from} – {to})",
    "history.period.12m": "Last 12 months",
    "history.period.3y": "Last 3 years",
    "history.period.5y": "Last 5 years",
    "history.period.all": "Whole history",
    "history.period.custom": "Custom",
    "history.periodChip": "{label} ({count})",
    "history.periodFrom": "From",
    "history.periodTo": "To",
    "history.periodUndated": "Include records without a date ({count})",
    "history.periodInvalid": "Enter a start date on or before the end date.",
    "history.shareLog": "Access log",
    "history.shareLogEmpty": "No opens yet",
    "history.shareEvent.unlocked": "Opened",
    "history.shareEvent.file_downloaded": "File downloaded",
    "history.shareEvent.exported": "Exported",
    "history.shareClient.chrome": "Chrome",
    "history.shareClient.firefox": "Firefox",
    "history.shareClient.safari": "Safari",
    "history.shareClient.edge": "Edge",
    "history.shareClient.other": "Other browser",
    "history.shareBanner": "Shared by the patient or their caregiver. Not verified by MedConcil. AI opinions are labeled and are not diagnoses.",
    "history.shareCodePrompt": "Enter the access code",
    "history.shareCodeLabel": "Access code",
    "history.shareCodeContinue": "Continue",
    "history.shareCodeOptional": "If you were not given a code, continue.",
    "history.shareInvalidCode": "That code is not valid.",
    "history.shareLocked": "Too many attempts. Try again later.",
    "history.shareNotFound": "This link is not available.",
    "history.shareDownloadPdf": "Download PDF",
    "history.shareDownloadFhir": "Download FHIR",
    "history.shareDownloadZip": "Download ZIP",
    "history.shareLoading": "Loading…",
    "history.shareViewFile": "Document",
    "history.shareCloseFile": "Close",

    // Patient form placeholders
    "patients.dosePlaceholder": "100 mg",
    "patients.freqPlaceholder": "2x/day",
  },
  
  ru: {
    // Login page
    "login.title": "МедКонсилиум",
    "login.subtitle": "Второе мнение ИИ перед решением",
    "login.welcome": "С возвращением",
    "login.signInMessage": "Войдите для доступа к рабочему пространству МедКонсилиума",
    "login.continueWithGoogle": "Войти через Google",
    "login.terms": "Входя в систему, вы соглашаетесь с Условиями использования и Политикой конфиденциальности",
    "login.feature1.title": "Мульти-модельный консенсус",
    "login.feature1.desc": "Запрос к GPT и Op одновременно",
    "login.feature2.title": "Защита персональных данных",
    "login.feature2.desc": "Автоматическое удаление конфиденциальных данных перед обработкой ИИ",
    "login.feature3.title": "Командная работа",
    "login.feature3.desc": "Полный аудит и контроль расходов для вашей организации",
    "login.subtitle": "Второе мнение ИИ перед решением",
    "login.welcome": "Добро пожаловать",
    "login.signInDesc": "Войдите, чтобы начать случай",
    "login.signInGoogle": "Войти через Google",
    "login.signingIn": "Вход...",
    "login.signInEmail": "Войти по Email",
    "login.password": "Пароль",
    "login.register": "Создать аккаунт",
    "login.noAccount": "Нет аккаунта?",
    "login.createAccount": "Создать",
    "login.hasAccount": "Уже есть аккаунт?",
    "login.signIn": "Войти",
    "login.or": "или",
    "login.checkEmail": "Проверьте почту для подтверждения",
    "login.error": "Ошибка входа",
    "login.oauthNotConfigured": "Этот способ входа недоступен в этой среде.",
    "login.forgotPassword": "Забыли пароль?",
    "login.rememberMe": "Запомнить меня",
    "login.emailVerified": "Email подтверждён. Войдите в аккаунт.",
    "login.resetSent": "Ссылка для сброса пароля отправлена на вашу почту",
    "login.enterEmailFirst": "Сначала введите email",
    "login.gdpr": "ФЗ-152",
    "login.pii": "ПДн защищены",
    "login.footer": "Информационное второе мнение — не диагноз и не экстренная помощь",

    // Legal
    "legal.termsNoticeSignin": "Входя в систему, вы соглашаетесь с условиями:",
    "legal.termsNoticeSignup": "Создавая аккаунт, вы соглашаетесь с условиями:",
    "legal.privacyPolicy": "Политика конфиденциальности",
    "legal.termsOfUse": "Условия использования",
    "legal.cookiePolicy": "Политика cookie",
    "legal.and": "и",
    "legal.redirecting": "Перенаправление на страницу с юридической информацией…",
    
    // Landing page
    "landing.eyebrow": "Второе мнение, не диагноз",
    "landing.heroTitle": "Когда нужно второе мнение перед решением",
    "landing.heroSubtitle": "Несколько медицинских ИИ вместе рассматривают ваш случай. Вы получаете консенсус, расхождения и вопросы для врача.",
    "landing.cta": "Начать случай",
    "landing.ctaSecondary": "Войти",
    "landing.signIn": "Войти",
    "landing.howItWorks": "Как это работает",
    "landing.step1Title": "Опишите случай",
    "landing.step1Desc": "Расскажите о симптомах, истории или вопросах простым языком. При необходимости приложите анализы или снимки.",
    "landing.step2Title": "ИИ обсуждают вместе",
    "landing.step2Desc": "Несколько медицинских моделей анализируют случай независимо, затем председатель формирует прозрачный отчёт.",
    "landing.step3Title": "Обсудите с врачом",
    "landing.step3Desc": "Скачайте PDF с консенсусом, неопределённостями и вопросами — поддержка решения, а не замена лечения.",
    "landing.notTitle": "Чем это не является",
    "landing.notDesc": "Не медкарта на всю жизнь, не носимые устройства, не постоянный чат-компаньон, не телемедицина и не экстренная помощь.",
    "landing.disclaimer": "Только для информации. Не диагноз. При срочных симптомах обращайтесь за экстренной помощью.",
    "landing.footer": "Всегда обсуждайте результаты с квалифицированным врачом.",

    // Sidebar & Navigation
    "nav.voice": "Голос",
    "nav.query": "Запрос",
    "nav.newCase": "Новый случай",
    "nav.myCases": "Медицинская история",
    "nav.history": "История",
    "nav.admin": "Админ",
    "nav.adminSection": "Управление",
    "nav.clinicalSupport": "Поддержка клинических решений",
    "nav.secondOpinion": "Второе мнение",
    "nav.account": "Аккаунт",
    "nav.settings": "Настройки",
    "nav.signOut": "Выйти",
    "nav.activeCouncil": "Активный Совет",
    "nav.plan": "план",
    "nav.healthProfile": "Профиль здоровья",

    // Care-circle subjects
    "subjects.me": "Я",
    "subjects.addPerson": "Добавить человека",
    "subjects.managePeople": "Управление людьми",
    "subjects.manageDesc": "Люди, чьим здоровьем вы управляете в этом аккаунте.",
    "subjects.caseFor": "Случай для:",
    "subjects.someoneElse": "Кто-то другой (разовый, без сохранения)",
    "subjects.oneOffHint": "Этот случай не будет сохранён в чьём-либо профиле здоровья.",
    "subjects.addAgeSex": "Добавьте возраст и пол для {name} — ответы будут точнее",
    "subjects.useMyProfile": "Использовать мой профиль здоровья ({summary})",
    "subjects.useNamedProfile": "Использовать профиль здоровья {name} ({summary})",
    "subjects.editProfile": "Редактировать профиль",
    "subjects.ageYears": "{age} {yearsLabel}",
    "subjects.ageMonths": "{age} {monthsLabel}",
    "subjects.notProvided": "не указаны",
    "subjects.relationship": "Кем приходится",
    "subjects.displayName": "Имя или прозвище",
    "subjects.displayNamePlaceholder": "например, Бабушка Анна",
    "subjects.birthMonth": "Месяц рождения",
    "subjects.birthMonthRequired": "Месяц рождения обязателен для детей младше 2 лет",
    "subjects.consentTitle": "Подтверждение согласия",
    "subjects.consentHint": "Укажите правовое основание для управления данными о здоровье этого человека.",
    "subjects.consent.guardian": "Я родитель или законный представитель несовершеннолетнего",
    "subjects.consent.representative": "Я законный представитель (доверенность / опека)",
    "subjects.consent.subject_consent": "Этот взрослый согласился, что я могу управлять его данными о здоровье",
    "subjects.consentReadonly": "Согласие: {basis} · дата {date}",
    "subjects.addValidation": "Нужны имя и подтверждение согласия",
    "subjects.created": "Человек добавлен",
    "subjects.safetyStepTitle": "Необязательно: лекарства и аллергии",
    "subjects.safetyStepHint": "Можно пропустить и добавить позже.",
    "subjects.safetyDone": "Готово",
    "subjects.createError": "Не удалось добавить",
    "subjects.limitReached": "Достигнут лимит людей в этом аккаунте.",
    "subjects.rel.self": "Я",
    "subjects.rel.child": "Ребёнок",
    "subjects.rel.parent": "Родитель",
    "subjects.rel.grandparent": "Дедушка / бабушка",
    "subjects.rel.partner": "Партнёр",
    "subjects.rel.sibling": "Брат / сестра",
    "subjects.rel.other_family": "Другой родственник",
    "subjects.rel.other": "Другое",
    "subjects.settings": "Настройки",
    "subjects.avatarColor": "Цвет аватара",
    "subjects.color.teal": "Бирюзовый",
    "subjects.color.amber": "Янтарный",
    "subjects.color.rose": "Розовый",
    "subjects.color.indigo": "Индиго",
    "subjects.color.emerald": "Изумрудный",
    "subjects.color.sky": "Небесный",
    "subjects.color.violet": "Фиолетовый",
    "subjects.color.orange": "Оранжевый",
    "subjects.saved": "Сохранено",
    "subjects.saveError": "Не удалось сохранить",
    "subjects.loadError": "Не удалось загрузить",
    "subjects.deletePerson": "Удалить человека",
    "subjects.deleteWarning": "Удаление человека также удалит все его случаи. Сначала экспортируйте, если нужна копия.",
    "subjects.deleteConfirm": "Удалить этого человека и все его случаи? Это нельзя отменить.",
    "subjects.deleted": "Человек удалён",
    "subjects.deleteError": "Не удалось удалить",
    "subjects.selfClearHint": "Чтобы очистить свой профиль здоровья, откройте Аккаунт.",
    "subjects.placeholderFor": "Опишите симптомы или вопрос {name}…",
    "subjects.filterAll": "Все люди",
    "subjects.filterOneOff": "Разовые случаи",

    // Voice page
    "voice.pageTitle": "Запись приёма",
    "voice.readyTitle": "Запись приёма",
    "voice.readyDesc": "Нажмите чтобы начать запись разговора с пациентом",
    "voice.consent": "Убедитесь что пациент дал согласие на запись",
    "voice.recordingLabel": "Запись идёт",
    "voice.transcribing": "Распознаём речь...",
    "voice.reviewLabel": "Транскрипт разговора — отредактируйте если нужно",
    "voice.send": "Отправить в Консилиум",
    "voice.reRecord": "Записать заново",
    "voice.noKey": "Groq API ключ не установлен",
    "voice.noKeyTitle": "Groq API ключ не установлен",
    "voice.noKeyDesc": "Добавьте ключ в разделе Админ для работы распознавания речи",
    "voice.goToAdmin": "Перейти в Админ",
    "voice.permDenied": "Доступ к микрофону запрещён. Проверьте настройки браузера.",
    "voice.noSupport": "Браузер не поддерживает запись аудио",
    "admin.groqKeyLabel": "Groq API Key (для распознавания голоса)",
    "admin.groqKeyDesc": "Ключ для распознавания речи через Groq Whisper. Хранится локально в браузере.",
    "admin.groqPersonalLabel": "Личный ключ (только этот браузер)",
    "admin.groqServerLabel": "Ключ по умолчанию (для всех пользователей)",
    "admin.groqServerActive": "Активен для всех",
    "admin.groqServerKeySet": "Серверный ключ настроен",
    "admin.groqServerKeyNotSet": "Серверный ключ не установлен",
    "admin.groqServerSaved": "Серверный ключ сохранён",
    "admin.groqServerError": "Не удалось сохранить серверный ключ",
    "admin.groqKeySaved": "Groq API ключ сохранён",
    "admin.groqKeyCleared": "Groq API ключ удалён",
    "admin.saved": "Сохранено",
    "admin.save": "Сохранить",

    // Query page
    "query.voiceCardTitle": "Запись приёма",
    "query.voiceCardDesc": "Запишите разговор с пациентом и отправьте в Консилиум",
    "query.chairmanStreaming": "Председатель формирует ответ...",
    "query.clinicalSummary": "Клиническое резюме",
    "query.clinicalSummaryGenerating": "Клиническое резюме (формируется…)",
    "query.title": "Новый случай",
    "query.placeholder": "Опишите ситуацию, симптомы или вопрос. При необходимости приложите анализы или снимки.",
    "query.piiNotice": "Конфиденциальные данные скрываются перед анализом ИИ",
    "query.submit": "Спросить консилиум",
    "scenario.groupLabel": "Быстрый разбор",
    "scenario.caption": "Есть документ? Выберите, что нужно, — консилиум его разберёт.",
    "scenario.askWithoutFile": "Спросить без файла",
    "scenario.attach.takePhoto": "Сфотографировать",
    "scenario.attach.choosePhotos": "Выбрать фото",
    "scenario.attach.chooseFiles": "Выбрать файлы",
    "scenario.attach.camera": "Камера",
    "scenario.summaryLabel": "Быстрый разбор: {name}",
    "scenario.lab_results.chip": "Разобрать анализы",
    "scenario.lab_results.name": "Разобраться в анализах",
    "scenario.lab_results.run": "Разобрать анализы",
    "scenario.lab_results.description": "Объяснит каждый показатель вне нормы и что делать дальше.",
    "scenario.lab_results.attach": "Прикрепите бланк анализов (PDF или фото).",
    "scenario.imaging.chip": "Расшифровать снимки",
    "scenario.imaging.name": "Расшифровать обследование (МРТ, КТ, УЗИ)",
    "scenario.imaging.run": "Расшифровать обследование",
    "scenario.imaging.description": "Простыми словами объяснит находки и заключение.",
    "scenario.imaging.attach": "Прикрепите заключение или снимки.",
    "scenario.treatment_plan.chip": "Проверить лечение",
    "scenario.treatment_plan.name": "Проверить план лечения",
    "scenario.treatment_plan.run": "Проверить план лечения",
    "scenario.treatment_plan.description": "Сверит назначенное лечение с диагнозом и профилем здоровья.",
    "scenario.treatment_plan.attach": "Прикрепите назначение или выписку.",
    "scenario.diagnosis.chip": "Проверить диагноз",
    "scenario.diagnosis.name": "Второе мнение о диагнозе",
    "scenario.diagnosis.run": "Проверить диагноз",
    "scenario.diagnosis.description": "Покажет, что подтверждает диагноз, что не сходится и что обсудить с врачом.",
    "scenario.diagnosis.attach": "Прикрепите заключение врача или результаты анализов.",
    "scenario.medications.chip": "Проверить лекарства",
    "scenario.medications.name": "Проверить лекарства",
    "scenario.medications.run": "Проверить лекарства",
    "scenario.medications.description": "Проверит взаимодействия, дозы и побочные эффекты с учётом профиля здоровья.",
    "scenario.medications.attach": "Прикрепите фото упаковок или рецепт.",
    "query.loading": "Рассматриваем ваш случай...",
    "query.consiliumWorking": "Медицинские ИИ рассматривают ваш случай...",
    "query.remaining": "осталось",
    "query.almostDone": "Почти готово...",
    "query.attachmentWaitNote": "Запросы с вложениями занимают больше времени — каждый консультант читает весь файл. Пожалуйста, не закрывайте страницу.",
    "query.stagePii": "Проверка ПДн",
    "query.stageModels": "Модели анализируют",
    "query.stageChairman": "Председатель синтезирует",
    "query.stageSaving": "Завершение",
    "query.success": "Консилиум ответил!",
    "query.stage1": "Индивидуальные ответы",
    "query.stage3": "Синтез Консилиума",
    "query.chairmanNote": "Консилиум рассмотрел все ответы и составил финальный ответ",
    "query.patientMessage": "Сообщение для пациента",
    "query.readyToSend": "Готово к проверке",
    "query.patientMessageNote": "Проверьте и отправьте пациенту. Написано понятным языком.",
    "query.physicianReview": "Резюме для врача",
    "query.internalOnly": "Внутреннее",
    "query.b2cSummary": "Итог",
    "query.b2cNextSteps": "Ваши следующие шаги",
    "query.b2cConsensus": "В чём модели согласны",
    "query.b2cDisagreements": "Различия и неопределённость",
    "query.b2cQuestions": "Вопросы вашему врачу",
    "query.b2cRedFlags": "Когда обращаться за помощью",
    "query.b2cResultTitle": "Ваше второе мнение",
    "query.b2cResultNote": "Несколько медицинских ИИ выбраны для разбора вашего случая:",
    "query.b2cFullResponse": "Полный ответ",
    "query.b2cFullResponseNote": "Не удалось разбить ответ на разделы, поэтому ниже показан полный текст.",
    "query.b2cDisclaimerTitle": "Важно",
    "query.b2cDisclaimer": "Это второе мнение, сформированное искусственным интеллектом, только для информации. Это не диагноз, не экстренная помощь и не замена врача. При неотложных симптомах немедленно обратитесь за экстренной помощью. Обязательно обсудите результаты с квалифицированным врачом.",
    "query.b2cDownloadReport": "Скачать отчёт",
    "query.b2cCopyReport": "Копировать отчёт",
    "query.tokens": "Токены",
    "query.yourCost": "Ваша стоимость",
    "query.piiRedacted": "ПДн скрыто",
    "query.models": "модели",
    "query.emptyTitle": "Начните новый случай",
    "query.emptyDesc": "Опишите, по какому вопросу нужно второе мнение. Несколько медицинских ИИ проанализируют случай и подготовят консенсус, расхождения и вопросы для врача.",
    "query.disclaimerAcknowledgment": "Я понимаю, что это второе мнение, сформированное искусственным интеллектом; это не диагноз, не экстренная медицинская помощь и не замена консультации врача. Я обсужу результаты с квалифицированным специалистом.",
    "query.disclaimerNotice": "Это второе мнение, сформированное искусственным интеллектом. Это не диагноз, не экстренная медицинская помощь и не замена консультации врача. Обсудите результаты с квалифицированным специалистом.",
    "query.perspectiveLabel": "ИИ-советник · {specialty}",
    "query.perspectiveFallback": "ИИ-советник",
    "query.perspectiveMarker": "ИИ",
    "query.perspectiveRow": "Позиции ИИ",
    "query.disclaimerRequired": "Подтвердите актуальное клиническое уведомление, прежде чем отправить случай.",
    "query.councilForThisQuery": "Консилиум для этого запроса",
    "query.councilSelectedCount": "Выбрано: {count}",
    "query.specialistsSelected": "Выбрано специалистов: {count}",
    "query.jumpToSection": "Перейти к разделу",
    "query.metadata": "Метаданные",
    "query.copyClinicalSummary": "Копировать клиническое резюме",
    "query.copyPatientMessage": "Копировать сообщение пациенту",
    "query.pdfPhysician": "PDF (врачу)",
    "query.pdfPatient": "PDF (пациенту)",
    "query.councilEmptyUsesDefault": "Врачи будут подобраны под ваш случай.",
    "query.councilAutomatic": "Врачи будут подобраны под ваш случай.",
    "query.councilGroupGeneralist": "Врачи общей практики",
    "query.councilGroupMedical": "Терапевтические специальности",
    "query.councilGroupSurgical": "Хирургические специальности",
    "query.councilGroupDiagnostic": "Диагностика",
    "query.councilSearchPlaceholder": "Поиск по имени или специальности",
    "query.councilClearAutomatic": "Сбросить (автоматически)",
    "query.councilSelectedForCase": "Подобраны под ваш случай",
    "query.councilExpandSpecialists": "Выбрать специалистов",
    "query.councilCollapseSpecialists": "Скрыть специалистов",
    "query.councilSelectionUpdated": "Состав консилиума обновлён — часть специалистов больше недоступна.",
    "query.freeCasesBanner": "Бесплатных кейсов осталось: {remaining} из {limit}",
    "query.trialDaysLeft": "Пробный период: осталось {days} {daysLabel}",
    "query.trialCasesLeft": "сегодня осталось {remaining} {casesLabel}",
    "query.trialLimitReached": "дневной лимит случаев исчерпан",
    "query.trialCaseUsed": "сегодняшний пробный случай использован",
    "query.increaseLimit": "Увеличить лимит",
    "unit.day.one": "день",
    "unit.day.few": "дня",
    "unit.day.many": "дней",
    "unit.case.one": "случай",
    "unit.case.few": "случая",
    "unit.case.many": "случаев",
    "unit.year.one": "год",
    "unit.year.few": "года",
    "unit.year.many": "лет",
    "unit.month.one": "месяц",
    "unit.month.few": "месяца",
    "unit.month.many": "месяцев",
    "unit.medication.one": "лекарство",
    "unit.medication.few": "лекарства",
    "unit.medication.many": "лекарств",
    "unit.allergy.one": "аллергия",
    "unit.allergy.few": "аллергии",
    "unit.allergy.many": "аллергий",
    "query.onboardingBonusCta": "Заполните профиль здоровья — получите 2 дополнительных кейса",
    "query.postTrialPaywallHint": "Пробный период закончился. Купите кейс, чтобы продолжить.",
    "query.trialDailyLimit": "Дневной лимит пробного периода исчерпан, попробуйте завтра.",
    "query.trialExpired": "Ваш пробный период завершён — купите кейс, чтобы продолжить.",
    "query.rateLimit": "Превышен лимит запросов",
    "query.addImage": "Фото",
    "query.addFile": "Файл",
    "query.fileTooLarge": "Файл слишком большой (макс. 25МБ)",
    "query.tooManyFiles": "Слишком много файлов (макс. {max}).",
    "query.attachmentsTooLarge": "Общий размер вложений слишком большой (макс. 50МБ).",
    "query.unsupportedFile": "Неподдерживаемый тип файла",
    "query.imageTooLarge": "Изображение слишком большое (макс. 10МБ)",
    "query.timeout": "Запрос превысил время ожидания. Попробуйте снова.",
    "query.followupTitle": "Уточняющие вопросы к консилиуму",
    "query.followupAnswer": "Ответ на уточнение",
    "query.followupPlaceholder": "Задайте уточняющий вопрос...",
    "query.followupError": "Ошибка при отправке вопроса",
    "query.followupLimitExceeded": "Достигнут лимит уточняющих вопросов для этого случая.",
    "query.followupLimitReached": "Вы использовали все уточняющие вопросы для этого случая. Начните новый случай, чтобы продолжить.",
    "query.followupStartNewCase": "Начать новый случай",
    "query.followupAttachments": "Вложения",
    "query.followupAttachmentsExhausted": "Достигнут лимит вложений для уточняющих вопросов по этому случаю.",
    "query.copied": "Скопировано",
    "query.copy": "Копировать",
    "query.downloadPdf": "Скачать PDF",
    "query.formatPdf": "PDF",
    "query.formatDocx": "DOCX",
    "query.pdfDownloaded": "PDF скачан",
    "query.pdfError": "Ошибка скачивания PDF",
    "query.pdfNoQuery": "Нет данных для скачивания — выполните запрос",
    "query.feedbackPrompt": "Было ли это полезно?",
    "query.feedbackHelpful": "Полезно",
    "query.feedbackNotHelpful": "Не полезно",
    "query.feedbackThanks": "Спасибо за отзыв",
    "query.showRationale": "Показать обоснование",
    "query.rationaleComingSoon": "Подробная привязка к доказательной базе скоро появится",
    "query.textSize": "Размер текста",
    "query.textSizeDecrease": "Уменьшить текст",
    "query.textSizeIncrease": "Увеличить текст",
    "query.patientExportAttestationTitle": "Подтверждение экспорта для пациента",
    "query.patientExportAttestation": "Я проверил(а) и адаптировал(а) это сообщение для моего пациента.",
    "query.patientExportConfirm": "Подтвердить и скачать",
    "query.docxPhysician": "Скачать DOCX для врача",
    "query.docxPatient": "Скачать DOCX для пациента",
    "query.evidence.strong": "Высокая доказательность",
    "query.evidence.moderate": "Умеренная доказательность",
    "query.evidence.limited": "Ограниченная доказательность",
    "query.evidence.consensus": "Консенсус",
    
    // Theme
    "theme.light": "Светлая",
    "theme.dark": "Тёмная",
    "theme.switchToLight": "Переключить на светлую тему",
    "theme.switchToDark": "Переключить на тёмную тему",
    "theme.clinicalAppearance": "Оформление рабочей области",
    "theme.clinicalAppearanceDesc": "Светлая или тёмная тема клинической области (страница входа не меняется)",
    
    // History page
    "history.title": "Медицинская история",
    "history.queries": "запросов",
    "history.exportCsv": "Экспорт CSV",
    "history.searchPlaceholder": "Поиск запросов...",
    "history.startDate": "Начальная дата",
    "history.endDate": "Конечная дата",
    "history.start": "Начало",
    "history.end": "Конец",
    "history.clear": "Очистить",
    "history.noQueries": "Пока нет запросов",
    "history.timestamp": "Время",
    "history.queryCol": "Запрос",
    "history.modelsCol": "Модели",
    "history.costCol": "Стоимость",
    "history.piiCol": "ПДн",
    "history.view": "Открыть",
    "history.queryDetails": "Детали случая",
    "history.caseSummary": "Кратко о случае",
    "history.exported": "История экспортирована!",
    
    // Admin page
    "admin.title": "Панель администратора",
    "admin.dashboard": "Дашборд",
    "admin.auditLog": "Журнал аудита",
    "admin.team": "Команда",
    "admin.settings": "Настройки",
    "admin.accessDenied": "Доступ запрещён",
    "admin.noPermission": "У вас нет прав для доступа к этой странице.",
    
    // Dashboard
    "dashboard.queriesThisMonth": "Запросов в этом месяце",
    "dashboard.totalSpend": "Общие расходы",
    "dashboard.activeUsers": "Активные пользователи",
    "dashboard.avgCostPerQuery": "Стоимость / запрос",
    "dashboard.queriesPerDay": "Запросов в день",
    "dashboard.spendByModel": "Расходы по моделям",
    "dashboard.topUsers": "Топ пользователей",
    "dashboard.user": "Пользователь",
    "dashboard.queries": "Запросы",
    "dashboard.spend": "Расходы",
    "dashboard.noData": "Пока нет данных",
    
    // Team
    "team.title": "Участники команды",
    "team.members": "участников",
    "team.inviteMembers": "Пригласить",
    "team.inviteTitle": "Пригласить участников",
    "team.inviteDesc": "Добавьте новых участников в вашу организацию",
    "team.emailAddresses": "Email адреса",
    "team.emailPlaceholder": "ivan@example.com, anna@example.com",
    "team.role": "Роль",
    "team.member": "Участник",
    "team.adminRole": "Админ",
    "team.auditor": "Аудитор",
    "team.owner": "Владелец",
    "team.cancel": "Отмена",
    "team.sendInvites": "Отправить приглашения",
    "team.joined": "Присоединился",
    "team.actions": "Действия",
    "team.pendingInvites": "Ожидающие приглашения",
    
    // Settings
    "settings.councilConfig": "Конфигурация Консилиума",
    "settings.councilConfigDesc": "Настройте ИИ-специалистов, участвующих в консилиуме",
    "settings.councilMembers": "Участники Консилиума",
    "settings.chairmanModel": "Модель Председателя",
    "settings.personasTitle": "Специалисты",
    "settings.personasDesc": "Включайте специалистов, выбирайте их базовую модель и состав консилиума по умолчанию",
    "settings.personaSpecialty": "Специализация",
    "settings.personaBackingModel": "Базовая модель",
    "settings.personaEnabled": "Включён",
    "settings.defaultCouncilMember": "В консилиуме по умолчанию",
    "settings.chairmanPersona": "Председатель",
    "settings.atLeastOneCouncilMember": "Выберите хотя бы одного включённого специалиста для консилиума по умолчанию",
    "settings.chairmanMustBeEnabled": "Специалист-председатель должен оставаться включённым",
    "settings.chairmanRequired": "Требуется специалист-председатель",
    "settings.personaModelRequired": "У каждого выбранного специалиста должна быть назначена базовая модель",
    "settings.councilNotConfigured": "Консилиум не настроен",
    "settings.piiRedaction": "Скрытие ПДн",
    "settings.piiRedactionDesc": "Автоматически скрывать ПДн перед обработкой ИИ",
    "settings.saveModelSettings": "Сохранить настройки моделей",
    "settings.budgetSettings": "Настройки бюджета",
    "settings.budgetSettingsDesc": "Контролируйте расходы вашей организации",
    "settings.monthlyLimit": "Месячный лимит",
    "settings.unlimitedPlaceholder": "Оставьте пустым для безлимита",
    "settings.alertThreshold": "Порог уведомления",
    "settings.currentSpend": "Текущие расходы",
    "settings.saveBudgetSettings": "Сохранить настройки бюджета",
    "settings.dangerZone": "Опасная зона",
    "settings.resetMonthlySpend": "Сбросить месячные расходы",
    "settings.resetDesc": "Установить расходы текущего месяца в 0",
    "settings.reset": "Сбросить",
    "settings.resetConfirmTitle": "Сбросить месячные расходы?",
    "settings.resetConfirmDesc": "Это установит счётчик расходов текущего месяца в 0. Это действие нельзя отменить.",
    "settings.saving": "Сохранение...",
    
    // Audit
    "audit.filterByEmail": "Фильтр по email...",
    "audit.startDate": "Начало",
    "audit.endDate": "Конец",
    "audit.export": "Экспорт",
    "audit.timestamp": "Дата и время",
    "audit.user": "Пользователь",
    "audit.query": "Запрос",
    "audit.models": "Модели",
    "audit.cost": "Стоимость",
    "audit.noLogs": "Записей аудита не найдено",
    "audit.exported": "Журнал аудита экспортирован!",
    "audit.loadError": "Не удалось загрузить журнал аудита",
    
    // Toast messages
    "toast.dashboardError": "Не удалось загрузить дашборд",
    "toast.teamError": "Не удалось загрузить команду",
    "toast.invitesSent": "Приглашения отправлены!",
    "toast.inviteError": "Не удалось отправить приглашения",
    "toast.roleUpdated": "Роль обновлена",
    "toast.roleError": "Не удалось обновить роль",
    "toast.userRemoved": "Пользователь удалён",
    "toast.userRemoveError": "Не удалось удалить пользователя",
    "toast.modelsSaved": "Настройки моделей сохранены!",
    "toast.modelsError": "Не удалось сохранить настройки",
    "toast.budgetSaved": "Настройки бюджета сохранены!",
    "toast.budgetError": "Не удалось сохранить настройки",
    "toast.budgetReset": "Расходы за месяц сброшены!",
    "toast.budgetResetError": "Не удалось сбросить расходы",
    "toast.settingsError": "Не удалось загрузить настройки",
    
    // Account
    "account.title": "Аккаунт",
    "account.profile": "Профиль",
    "account.organization": "Организация",
    "account.billing": "Оплата",
    "account.billingDesc": "Управление подпиской и оплатой",
    "account.apiKey": "API Ключ",
    "account.apiKeyDesc": "Используйте этот ключ для программного доступа к МедКонсилиуму",
    "account.regenerate": "Перегенерировать API Ключ",
    "account.regenerateConfirmTitle": "Перегенерировать API Ключ?",
    "account.regenerateConfirmDesc": "Ваш текущий API ключ будет немедленно аннулирован. Все приложения, использующие старый ключ, перестанут работать.",
    "account.regenerating": "Генерация...",
    "account.exampleUsage": "Пример использования:",
    "account.upgrade": "Улучшить",
    "account.billingComingSoon": "Интеграция оплаты скоро. Свяжитесь с поддержкой для улучшения тарифа.",

    // Health profile (B2C)
    "healthProfile.tierATitle": "Базовая информация о здоровье",
    "healthProfile.tierADesc": "Базовые сведения помогают консилиуму точнее подобрать рекомендации. Это не диагноз.",
    "healthProfile.tierARequired": "Укажите год рождения и пол при рождении",
    "healthProfile.disclaimerRequired": "Подтвердите уведомление о втором мнении, чтобы продолжить",
    "healthProfile.tierASaved": "Базовый профиль сохранён",
    "healthProfile.extendedSaved": "Профиль здоровья сохранён",
    "healthProfile.saveError": "Не удалось сохранить профиль здоровья",
    "healthProfile.saveIncomplete": "Часть сведений о здоровье не сохранилась. Попробуйте ещё раз.",
    "healthProfile.birthYear": "Год рождения",
    "healthProfile.sexAtBirth": "Пол при рождении",
    "healthProfile.genderIdentity": "Гендерная идентичность (необязательно)",
    "healthProfile.genderIdentityPlaceholder": "Необязательно",
    "healthProfile.pregnancyStatus": "Беременность",
    "healthProfile.selectPlaceholder": "Выберите…",
    "healthProfile.continue": "Продолжить",
    "healthProfile.privacyNote": "Ответы используются только как контекст для ваших кейсов.",
    "healthProfile.privacyLink": "Политика конфиденциальности",
    "healthProfile.sex.female": "Женский",
    "healthProfile.sex.male": "Мужской",
    "healthProfile.sex.intersex": "Интерсекс",
    "healthProfile.sex.prefer_not_to_say": "Предпочитаю не указывать",
    "healthProfile.pregnancy.not_applicable": "Не применимо",
    "healthProfile.pregnancy.yes": "Да",
    "healthProfile.pregnancy.possibly": "Возможно",
    "healthProfile.pregnancy.prefer_not_to_say": "Предпочитаю не указывать",
    "healthProfile.extendedTitle": "Профиль здоровья",
    "healthProfile.units.label": "Единицы",
    "healthProfile.units.metric": "Метрические",
    "healthProfile.units.imperial": "Имперские",
    "healthProfile.heightCm": "Рост (см)",
    "healthProfile.heightFt": "Рост (футы)",
    "healthProfile.heightIn": "Рост (дюймы)",
    "healthProfile.weightKg": "Вес (кг)",
    "healthProfile.weightLb": "Вес (фунты)",
    "healthProfile.smoking": "Курение",
    "healthProfile.alcohol": "Алкоголь",
    "healthProfile.chronicConditions": "Хронические заболевания",
    "healthProfile.chronicConditionsOther": "Другие заболевания",
    "healthProfile.medications": "Лекарства",
    "healthProfile.allergies": "Аллергии",
    "healthProfile.surgeries": "Недавние операции / процедуры",
    "healthProfile.activityLevel": "Уровень активности",
    "healthProfile.activityDays": "Активных дней в неделю",
    "healthProfile.sleepHours": "Обычный сон (часы)",
    "healthProfile.sleepRegular": "Регулярность сна",
    "healthProfile.notesOther": "Что ещё важно",
    "healthProfile.listPlaceholder": "Через запятую",
    "healthProfile.contextFor": "Профиль здоровья:",
    "healthProfile.section.safety": "Безопасность",
    "healthProfile.section.history": "Анамнез",
    "healthProfile.section.lifestyle": "Образ жизни",
    "healthProfile.sectionDesc.safety": "Лекарства и аллергии этого человека.",
    "healthProfile.sectionDesc.history": "Заболевания и недавние процедуры.",
    "healthProfile.sectionDesc.lifestyle": "Показатели и повседневные привычки.",
    "healthProfile.question.medications": "Постоянные лекарства?",
    "healthProfile.question.allergies": "Аллергии?",
    "healthProfile.question.chronic_conditions": "Хронические заболевания?",
    "healthProfile.question.surgeries_recent": "Операция или процедура за последний год?",
    "healthProfile.yes": "Да",
    "healthProfile.no": "Нет",
    "healthProfile.skip": "Пропустить",
    "healthProfile.more": "Ещё",
    "healthProfile.saveSection": "Сохранить",
    "healthProfile.safetyCardPrompt": "Есть лекарства или аллергии у {name}?",
    "healthProfile.safetyCardHint": "Спрашиваем, чтобы рекомендации по лекарствам были точными и не навредили вам.",
    "healthProfile.saveAndContinue": "Сохранить и продолжить",
    "healthProfile.skipForNow": "Пропустить пока",
    "healthProfile.includeInCase": "Включить мой профиль здоровья в этот кейс",
    "healthProfile.completeTierALink": "Заполнить базовый профиль",
    "healthProfile.accountTitle": "Профиль здоровья",
    "healthProfile.accountDesc": "Демография и необязательный профиль здоровья как контекст кейсов.",
    "healthProfile.saveTierA": "Сохранить базовые данные",
    "healthProfile.saveExtended": "Сохранить профиль здоровья",
    "healthProfile.deleteDataNote": "Вы можете удалить сохранённый профиль здоровья в любой момент.",
    "healthProfile.clearProfile": "Очистить профиль здоровья",
    "healthProfile.cleared": "Профиль здоровья очищен",
    "healthProfile.smoking.never": "Никогда",
    "healthProfile.smoking.former": "В прошлом",
    "healthProfile.smoking.current": "Сейчас",
    "healthProfile.alcohol.none": "Нет",
    "healthProfile.alcohol.occasional": "Иногда",
    "healthProfile.alcohol.regular": "Регулярно",
    "healthProfile.alcohol.prefer_not_to_say": "Предпочитаю не указывать",
    "healthProfile.activity.sedentary": "Малоподвижный",
    "healthProfile.activity.light": "Лёгкий",
    "healthProfile.activity.moderate": "Умеренный",
    "healthProfile.activity.vigorous": "Интенсивный",
    "healthProfile.sleep.regular": "В основном регулярный",
    "healthProfile.sleep.irregular": "Нерегулярный",
    "healthProfile.sleep.prefer_not_to_say": "Предпочитаю не указывать",

    "billing.paywallTitle": "Продолжить с оплатой",
    "billing.paywallDesc": "Бесплатная квота использована. Купите один кейс или оформите месячный план.",
    "billing.buyOneCase": "Купить один кейс",
    "billing.subscribeMonthly": "Месячный план",
    "billing.getMoreCases": "Получить ещё кейсы",
    "billing.viewPlans": "Смотреть тарифы",
    "billing.freeQuotaExhausted": "Бесплатная квота исчерпана — оплатите, чтобы продолжить.",
    "billing.subscriptionExhausted": "Месячная квота исчерпана — продлите или купите кейс.",
    "billing.paymentRequired": "Для нового кейса требуется оплата.",
    "billing.trialSummary": "Пробный период сегодня: {remaining}/{limit} · до {ends}",
    "billing.onboardingBonusRemaining": "Бонусных кейсов за онбординг: {count}",
    "billing.onboardingBonusCta": "Заполните профиль здоровья — получите 2 дополнительных кейса",
    "billing.postTrialPaywallHint": "Пробный период закончился — купите кейс или оформите подписку.",
    "billing.checkoutUnavailable": "Онлайн-оплата пока недоступна. Свяжитесь с поддержкой.",
    "billing.checkoutError": "Не удалось начать оплату. Попробуйте снова.",
    "billing.successTitle": "Оплата получена",
    "billing.successDesc": "Баланс обновится после подтверждения платежа.",
    "billing.successBalance": "Доступно: {free} бесплатных · {paid} предоплаченных · {sub} по подписке",
    "billing.backToCases": "К новому кейсу",
    "billing.cancelTitle": "Оплата отменена",
    "billing.cancelDesc": "Списания не было. Можете попробовать снова.",
    "billing.accountBalance": "Доступные кейсы — бесплатные: {free}, предоплата: {paid}, подписка: {sub}",
    "billing.currentPlan": "Тариф",
    
    // Onboarding
    "onboarding.createOrg": "Создать организацию",
    "onboarding.setupWorkspace": "Настройте рабочее пространство",
    "onboarding.configureCouncil": "Настроить Консилиум",
    "onboarding.selectModels": "Выберите модели ИИ",
    "onboarding.setBudget": "Установить бюджет",
    "onboarding.controlSpending": "Контроль расходов",
    "onboarding.inviteTeam": "Пригласить команду",
    "onboarding.addCollaborators": "Добавить коллег",
    "onboarding.createOrgTitle": "Создайте вашу организацию",
    "onboarding.createOrgDesc": "Это будет рабочее пространство вашей команды в МедКонсилиуме",
    "onboarding.orgName": "Название организации",
    "onboarding.orgNamePlaceholder": "ООО Компания",
    "onboarding.workspaceUrl": "URL рабочего пространства",
    "onboarding.createOrgBtn": "Создать организацию",
    "onboarding.creating": "Создание...",
    "onboarding.configureCouncilTitle": "Настройте ваш Консилиум",
    "onboarding.configureCouncilDesc": "Выберите модели ИИ для участия в консенсусе",
    "onboarding.chairmanNote": "Эта модель синтезирует финальный консенсусный ответ",
    "onboarding.back": "Назад",
    "onboarding.continue": "Продолжить",
    "onboarding.setBudgetTitle": "Установите лимиты расходов",
    "onboarding.setBudgetDesc": "Контролируйте ежемесячные расходы на ИИ",
    "onboarding.budgetNote": "Установите месячный лимит. Оставьте пустым для безлимита.",
    "onboarding.alertNote": "Получайте уведомления при достижении этого процента бюджета",
    "onboarding.skip": "Пропустить",
    "onboarding.inviteTeamTitle": "Пригласите команду",
    "onboarding.inviteTeamDesc": "Добавьте коллег для совместной работы с запросами Консилиума",
    "onboarding.separateEmails": "Разделяйте email адреса запятыми",
    "onboarding.defaultRole": "Роль по умолчанию",
    "onboarding.skipFinish": "Пропустить и завершить",
    "onboarding.completeSetup": "Завершить настройку",
    "onboarding.settingUp": "Настройка...",
    
    // Common
    "common.loading": "Загрузка...",
    "common.error": "Ошибка",
    "common.success": "Успешно",
    "common.save": "Сохранить",
    "common.cancel": "Отмена",
    "common.delete": "Удалить",
    "common.edit": "Редактировать",
    "common.close": "Закрыть",
    "common.back": "Назад",
    "common.confirm": "Подтвердить",
    "common.authError": "Сессия истекла. Пожалуйста, войдите снова.",
    "common.language": "Язык",
    "common.english": "English",
    "common.spanish": "Español",
    "common.arabic": "العربية",
    "common.french": "Français",
    "common.russian": "Русский",

    "login.displayNameOptional": "Имя (необязательно)",
    "login.signUpTitle": "Регистрация",
    "login.signUpWithEmail": "Регистрация по email",
    "login.signUpDesc": "Создайте аккаунт для доступа к консилиуму",

    "nav.patients": "Пациенты",
    "nav.platform": "Платформа",
    "nav.newQuery": "Новый случай",
    "sidebar.collapse": "Свернуть боковую панель",
    "sidebar.expand": "Развернуть боковую панель",
    "sidebar.openMenu": "Открыть меню",
    "query.newQuery": "Новый запрос",
    "query.filesOnly": "Проанализируй прикреплённые документы и изображения.",
    "query.confirmNewQuery": "Запрос ещё обрабатывается. Начать новый?",
    "query.confirmNewQueryForce": "Запрос ещё обрабатывается. Вы уверены, что хотите начать новый?",

    "reset.invalidLink": "Неверная ссылка",
    "reset.tokenMissing": "Токен сброса пароля отсутствует.",
    "reset.backToLogin": "Вернуться ко входу",
    "reset.passwordUpdated": "Пароль обновлён",
    "reset.canSignIn": "Теперь вы можете войти с новым паролем.",
    "reset.signIn": "Войти",
    "reset.newPassword": "Новый пароль",
    "reset.enterNewPassword": "Введите новый пароль для вашего аккаунта",
    "reset.confirmPassword": "Подтвердите пароль",
    "reset.savePassword": "Сохранить пароль",
    "reset.passwordMin": "Пароль минимум 6 символов",
    "reset.passwordMismatch": "Пароли не совпадают",
    "reset.success": "Пароль успешно обновлён",
    "reset.linkInvalid": "Ссылка недействительна или истекла",
    "reset.failed": "Ошибка сброса пароля",

    "patients.title": "Карты пациентов",
    "patients.newPatient": "Новый пациент",
    "patients.searchPlaceholder": "Поиск по имени...",
    "patients.noPatients": "Нет пациентов",
    "patients.name": "Имя",
    "patients.birthDate": "Дата рождения",
    "patients.gender": "Пол",
    "patients.genderMale": "М",
    "patients.genderFemale": "Ж",
    "patients.actions": "Действия",
    "patients.open": "Открыть",
    "patients.loadError": "Не удалось загрузить карту",
    "patients.nameRequired": "Укажите ФИО",
    "patients.saved": "Карта сохранена",
    "patients.saveError": "Ошибка сохранения",
    "patients.deleted": "Карта удалена",
    "patients.deleteError": "Ошибка удаления",
    "patients.back": "Назад",
    "patients.edit": "Редактировать",
    "patients.delete": "Удалить",
    "patients.newPatientTitle": "Новый пациент",
    "patients.editPatientTitle": "Редактирование карты",
    "patients.save": "Сохранить",
    "patients.saving": "Сохранение...",
    "patients.personalInfo": "Личные данные",
    "patients.fullName": "ФИО",
    "patients.phone": "Телефон",
    "patients.email": "Email",
    "patients.medicalProfile": "Медицинский профиль",
    "patients.bloodType": "Группа крови",
    "patients.allergies": "Аллергии",
    "patients.diagnoses": "Хронические диагнозы",
    "patients.medications": "Текущие препараты",
    "patients.surgeries": "Хирургический анамнез",
    "patients.familyHistory": "Семейный анамнез",
    "patients.notes": "Заметки",
    "patients.documents": "Документы",
    "patients.sessions": "Сессии консилиума",
    "patients.upload": "Загрузить",
    "patients.askConsilium": "Спросить консилиум",
    "patients.deleteConfirmTitle": "Удалить карту?",
    "patients.deleteConfirmDesc": "Это действие нельзя отменить.",
    "patients.cancel": "Отмена",
    "patients.notFound": "Пациент не найден",
    "patients.genderMaleFull": "Мужской",
    "patients.genderFemaleFull": "Женский",
    "patients.consilium": "Консилиум",
    "patients.profile": "Профиль",
    "patients.contact": "Контакты",
    "patients.snils": "СНИЛС",
    "patients.snilsPlaceholder": "123-456-789 00",
    "patients.mrn": "MRN",
    "patients.mrnPlaceholder": "12345678",
    "patients.phonePlaceholderUs": "+1 (555) 555-5555",
    "patients.phonePlaceholderCis": "+7...",
    "patients.created": "Создана",
    "patients.diagnosis": "Диагноз",
    "patients.status": "Статус",
    "patients.medication": "Препарат",
    "patients.dose": "Доза",
    "patients.frequency": "Частота",
    "patients.patientDocuments": "Документы пациента",
    "patients.titleCol": "Название",
    "patients.category": "Категория",
    "patients.date": "Дата",
    "patients.size": "Размер",
    "patients.noDocuments": "Документов нет",
    "patients.mode": "Режим",
    "patients.docUploaded": "Документ загружен",
    "patients.uploadError": "Ошибка загрузки",
    "patients.docDeleted": "Документ удалён",
    "patients.add": "Добавить",
    "patients.searchNamePhone": "Поиск по ФИО или телефону...",
    "patients.search": "Найти",
    "patients.birthDateShort": "Дата рожд.",
    "patients.genderShort": "Пол",
    "patients.noPatientsYet": "Пациентов пока нет",
    "patients.noSessionsYet": "Сессий ещё нет",
    "patients.deleteCardTitle": "Удалить карту пациента?",
    "patients.cardUpdated": "Карта обновлена",
    "patients.patientCreated": "Пациент создан",
    "patients.editCard": "Редактирование карты",
    "patients.basicInfo": "Основные данные",
    "patients.genderOther": "Другой",
    "patients.noAllergies": "Аллергий не указано",
    "patients.substance": "Вещество",
    "patients.severity": "Тяжесть",
    "patients.severityMild": "Лёгкая",
    "patients.severityModerate": "Умеренная",
    "patients.severitySevere": "Тяжёлая",
    "patients.reaction": "Реакция",
    "patients.noDiagnoses": "Диагнозов нет",
    "patients.nameLabel": "Название",
    "patients.icd10": "МКБ-10",
    "patients.noMedications": "Препаратов нет",
    "patients.noSurgeries": "Операций не было",
    "patients.hospital": "Больница",
    "patients.familyHistoryHint": "Семейный анамнез (через запятую)",
    "patients.additionalNotes": "Дополнительные заметки",
    "patients.statusActive": "Активен",
    "patients.statusRemission": "Ремиссия",
    "patients.statusResolved": "Разрешён",
    "patients.surgery": "Операция",
    "patients.familyNotes": "Семейный анамнез и заметки",
    "patients.familyExample": "СД 2 типа (мать)",
    "patients.additionalNotesPlaceholder": "Дополнительная информация...",

    // Onboarding
    "onboarding.stepOrg": "Создание организации",
    "onboarding.stepCouncil": "Настройка консилиума",
    "onboarding.stepBudget": "Бюджет",
    "onboarding.stepTeam": "Команда",
    "onboarding.createOrg": "Создайте организацию",
    "onboarding.createOrgDesc": "Укажите название вашей клиники или компании",
    "onboarding.orgName": "Название организации",
    "onboarding.orgNamePlaceholder": "Моя клиника",
    "onboarding.workspaceUrl": "URL рабочего пространства",
    "onboarding.slugPlaceholder": "moya-klinika",
    "onboarding.createOrgBtn": "Создать организацию",
    "onboarding.creating": "Создание...",
    "onboarding.orgCreated": "Организация создана!",
    "onboarding.fillFields": "Заполните все поля",
    "onboarding.configCouncil": "Настройте консилиум",
    "onboarding.configCouncilDesc": "Выберите AI-модели для консенсуса",
    "onboarding.councilMembers": "Участники консилиума",
    "onboarding.chairmanModel": "Модель-председатель",
    "onboarding.chairmanModelDesc": "Эта модель синтезирует итоговый консенсусный ответ",
    "onboarding.continue": "Продолжить",
    "onboarding.setBudget": "Установите лимиты расходов",
    "onboarding.setBudgetDesc": "Контролируйте ежемесячные расходы на ИИ",
    "onboarding.monthlyBudget": "Месячный бюджет",
    "onboarding.budgetPlaceholder": "Оставьте пустым для безлимита",
    "onboarding.budgetHint": "Установите месячный лимит. Оставьте пустым для безлимита.",
    "onboarding.alertThreshold": "Порог уведомления",
    "onboarding.alertHint": "Уведомление при достижении этого процента бюджета",
    "onboarding.skip": "Пропустить",
    "onboarding.inviteTeam": "Пригласите команду",
    "onboarding.inviteTeamDesc": "Добавьте коллег для совместной работы",
    "onboarding.emailAddresses": "Email-адреса",
    "onboarding.emailsHint": "Разделяйте адреса запятыми",
    "onboarding.defaultRole": "Роль по умолчанию",
    "onboarding.roleMember": "Участник",
    "onboarding.roleAdmin": "Администратор",
    "onboarding.roleAuditor": "Аудитор",
    "onboarding.skipFinish": "Пропустить и завершить",
    "onboarding.completeSetup": "Завершить настройку",
    "onboarding.settingUp": "Настройка...",
    "onboarding.setupComplete": "Настройка завершена!",
    "onboarding.startBtn": "Начать работу",

    // Patient picker / confirm modals
    "patient.pickTitle": "Выбор пациента",
    "patient.searchPlaceholder": "Имя или телефон...",
    "patient.notFound": "Пациент не найден",
    "patient.registerNew": "Зарегистрировать нового пациента",
    "patient.newTitle": "Новый пациент",
    "patient.fullName": "ФИО",
    "patient.fullNamePlaceholder": "Иванов Иван Иванович",
    "patient.birthDate": "Дата рождения",
    "patient.gender": "Пол",
    "patient.genderSelect": "Выберите",
    "patient.genderMale": "Мужской",
    "patient.genderFemale": "Женский",
    "patient.genderOther": "Другой",
    "patient.phone": "Телефон",
    "patient.phonePlaceholderUs": "+1 (555) 555-5555",
    "patient.phonePlaceholderCis": "+7 916 123-45-67",
    "patient.email": "Email",
    "patient.emailPlaceholder": "patient@email.com",
    "patient.create": "Создать",
    "patient.errNameMin": "Минимум 2 слова (Фамилия Имя)",
    "patient.errRequired": "Обязательное поле",
    "patient.errFutureDate": "Дата в будущем",
    "patient.errInvalidDate": "Некорректная дата",
    "patient.errPhoneFormatUs": "Формат: +1XXXXXXXXXX",
    "patient.errPhoneFormatCis": "Формат: +7XXXXXXXXXX",
    "patient.created": "Пациент зарегистрирован",
    "patient.createError": "Ошибка создания пациента",
    "patient.confirmTitle": "Создать запрос для",
    "patient.confirmYes": "Да, создать",
    "patient.confirmChange": "Выбрать другого",

    // Query page (patient + downloads)
    "query.changePatient": "Сменить пациента",
    "query.patientLabel": "Пациент:",
    "query.docxDownloaded": "DOCX скачан",
    "query.downloadError": "Ошибка скачивания",
    "query.copyError": "Не удалось скопировать",

    // Auth callback
    "auth.authorizing": "Авторизация...",
    "auth.authError": "Ошибка авторизации",

    // Account page (additional)
    "account.loadError": "Не удалось загрузить аккаунт",
    "account.keyRegenerated": "API ключ перегенерирован!",
    "account.keyRegenerateError": "Не удалось перегенерировать ключ",
    "account.keyCopied": "API ключ скопирован!",
    "account.createdLabel": "Создана",
    "account.orgId": "ID организации",
    "account.signOut": "Выйти",
    "account.cancel": "Отмена",
    "account.regenerateShort": "Перегенерировать",
    "account.planSuffix": "план",
    "account.planTeamDesc": "До 25 пользователей, безлимит запросов",
    "account.planBusinessDesc": "До 150 пользователей, доступен SSO",
    "account.planEnterpriseDesc": "Безлимит пользователей, индивидуальные функции",
    "account.clinicProfile": "Профиль клиники",
    "account.clinicProfileDesc": "Данные для шапки экспортируемых документов (PDF/DOCX)",
    "account.clinicName": "Название клиники",
    "account.clinicNamePlaceholder": "Городская клиника №1",
    "account.doctorName": "ФИО врача",
    "account.doctorNamePlaceholder": "Иванов И.И., к.м.н., терапевт",
    "account.clinicSaved": "Профиль клиники сохранён",
    "account.clinicSaveError": "Ошибка сохранения",
    "account.save": "Сохранить",
    "account.deleteAccount": "Удалить аккаунт",
    "account.deleteAccountTitle": "Удалить аккаунт?",
    "account.deleteAccountDesc": "Аккаунт, профиль здоровья, случаи и вложения будут удалены безвозвратно.",
    "account.deleteAccountPassword": "Введите пароль для подтверждения",
    "account.deleteAccountPasswordPlaceholder": "Пароль",
    "account.deleteAccountConfirm": "Удалить навсегда",
    "account.deleteAccountSuccess": "Аккаунт удалён",
    "account.deleteAccountError": "Не удалось удалить аккаунт",
    "account.deleteAccountReauth": "Войдите снова и повторите в течение 5 минут",

    // Platform dashboard
    "platform.title": "Платформа",
    "platform.totalUsers": "Всего пользователей",
    "platform.inPeriod": "за период",
    "platform.queriesToday": "Запросов сегодня",
    "platform.spendPeriod": "Расход за период",
    "platform.perQuerySuffix": "запрос",
    "platform.errors": "Ошибки",
    "platform.registrations": "Регистрации",
    "platform.registrationsBar": "Регистраций",
    "platform.yandex": "Яндекс",
    "platform.noData": "Нет данных",
    "platform.topUsers": "Топ пользователей по расходу",
    "platform.user": "Пользователь",
    "platform.clinic": "Клиника",
    "platform.owner": "Владелец",
    "platform.queries": "Запросов",
    "platform.spend": "Расход",
    "platform.spendByModel": "Расход по моделям",
    "platform.consultations": "Запросы за период",
    "platform.consultationsBar": "Запросов",
    "platform.clinicsTitle": "Клиники и тарифы",
    "platform.clinicsDesc": "Назначьте partner, trial, paid или expired для организации.",
    "platform.usersTitle": "Пользователи и доступ",
    "platform.usersDesc": "Partner — безлимитные кейсы. User — обычные лимиты free и оплаты.",
    "platform.accessTier": "Доступ",
    "platform.registered": "Регистрация",
    "platform.tier.user": "User",
    "platform.tier.partner": "Partner",
    "platform.tierSaved": "Доступ обновлён",
    "platform.tierError": "Не удалось обновить доступ",
    "platform.plan": "Тариф",
    "platform.actions": "Действия",
    "platform.savePlan": "Сохранить",
    "platform.entitlementSaved": "Тариф обновлён",
    "platform.entitlementError": "Не удалось обновить тариф",
    "platform.plan.trial": "Пробный",
    "platform.plan.partner": "Партнёр",
    "platform.plan.paid": "Платный",
    "platform.plan.expired": "Истёк",
    "period.today": "Сегодня",
    "period.week": "7 дней",
    "period.month": "30 дней",
    "period.all": "Всё время",

    // History page (additional)
    "history.synthesis": "Итог",
    "history.forPatient": "Для пациента",
    "history.forDoctor": "Для врача",
    "history.pdfForPatient": "PDF для пациента",
    "history.pdfForDoctor": "PDF для врача",
    "history.docxDownloaded": "DOCX скачан",
    "history.pdfDownloaded": "PDF скачан",
    "history.downloadError": "Ошибка скачивания",
    "history.addRecord": "Добавить запись",
    "history.summary": "Сводка",
    "history.timeline": "Хронология",
    "history.uploadDocument": "Загрузить документ",
    "history.filesSelected": "Выбрано файлов: {count}",
    "history.tooManyFiles": "К одной записи можно прикрепить не больше {max} файлов.",
    "history.addEntry": "Добавить запись без файла",
    "history.category": "Категория",
    "history.eventDate": "Дата события",
    "history.datePrecision": "Точность даты",
    "history.precision.day": "День",
    "history.precision.month": "Месяц",
    "history.precision.year": "Год",
    "history.dateUnknown": "Дата неизвестна",
    "history.titleField": "Название",
    "history.facility": "Учреждение или врач",
    "history.notes": "Заметки",
    "history.details": "Подробности",
    "history.save": "Сохранить",
    "history.cancel": "Отмена",
    "history.delete": "Удалить",
    "history.deleteConfirm": "Удалить эту запись? Это нельзя отменить.",
    "history.deleteCaseDocuments.one": "Также удалить документ, сохранённый из этого случая",
    "history.deleteCaseDocuments.few": "Также удалить {n} документа, сохранённых из этого случая",
    "history.deleteCaseDocuments.many": "Также удалить {n} документов, сохранённых из этого случая",
    "history.attachedFiles.one": "{n} прикреплённый файл",
    "history.attachedFiles.few": "{n} прикреплённых файла",
    "history.attachedFiles.many": "{n} прикреплённых файлов",
    "history.dateFromCase": "Дата взята из случая. Измените её, если файл старше.",
    "history.fromCase": "Из случая: {title}",
    "history.fromCaseDated": "Из случая от {date}",
    "history.fromCaseUntitled": "Из случая",
    "history.fromCasePending": "Из незавершённого случая",
    "history.fromCaseDeleted": "Из удалённого случая",
    "history.filePages": "{n} стр.",
    "history.edit": "Изменить",
    "history.download": "Скачать",
    "history.previewUnavailable": "Предпросмотр недоступен",
    "history.noRecords": "Записей пока нет",
    "history.noOneOff": "Нет разовых случаев",
    "history.aiSecondOpinion": "Второе мнение ИИ — не медицинская запись",
    "history.recordCount": "Записей: {count}",
    "history.storageUsage": "Занято {used} МБ из {limit} МБ",
    "history.conditions": "Состояния",
    "history.medications": "Лекарства",
    "history.allergies": "Аллергии",
    "history.noneReported": "Не указано",
    "history.notProvided": "Пока не добавлено",
    "history.review": "Проверить",
    "history.latest": "Последняя",
    "history.consentTitle": "Хранение медицинских записей",
    "history.consentAccept": "Согласен",
    "history.consentRequired": "Перед добавлением записи примите согласие на хранение.",
    "history.duplicateWarning": "Файл с таким содержимым уже есть в истории. Обе копии сохранены.",
    "history.storageFull": "Хранилище медицинской истории заполнено. Удалите большой файл и повторите.",
    "history.caseFilesNotSavedTitle": "Файлы не сохранены",
    "history.caseFilesNotSaved": "Эти файлы не сохранены в медицинскую историю. Добавьте их позже в разделе «Медицинская история».",
    "history.caseFilesNotSavedOk": "OK",
    "history.shareSessionExpired": "Сеанс просмотра завершён. Введите код доступа ещё раз.",
    "history.saved": "Запись сохранена",
    "history.saveError": "Не удалось сохранить запись",
    "history.loadError": "Не удалось загрузить медицинскую историю",
    "history.deleted": "Запись удалена",
    "history.saveFilesToHistory": "Сохранить эти файлы в медицинскую историю: {name}",
    "history.oneOffCases": "Разовые случаи",
    "history.tierA": "Возраст и пол при рождении",
    "history.fileUnavailable": "Файл больше недоступен",
    "history.updated": "Обновлено {date}",
    "history.source.user_upload": "Загружено вами",
    "history.source.user_entered": "Введено вами",
    "history.source.case_attachment": "Сохранено из случая",
    "history.source.medconcil_case": "Случай MedConcil",
    "history.category.visit_note": "Запись о визите",
    "history.category.discharge_summary": "Выписка",
    "history.category.lab_result": "Анализ",
    "history.category.imaging_report": "Заключение по снимку",
    "history.category.prescription": "Назначение",
    "history.category.procedure": "Процедура",
    "history.category.immunization": "Прививка",
    "history.category.referral": "Направление",
    "history.category.other": "Другое",
    "history.category.ai_second_opinion": "Второе мнение ИИ",
    "history.export": "Экспорт",
    "history.exportTitle": "Экспорт медицинской истории",
    "history.exportDescription": "Клиническая сводка для врача. MedConcil не проверяет эти записи.",
    "history.exportFormat": "Формат",
    "history.format.pdf": "Сводка PDF",
    "history.format.fhir": "Международная сводка пациента FHIR (JSON)",
    "history.format.zip": "ZIP: PDF, FHIR и документы",
    "history.includeAiOpinions": "Включить вторые мнения ИИ",
    "history.exportAiHint": "Вторые мнения ИИ не попадут в экспорт, пока это не отмечено.",
    "history.exportWholeHistory": "Вся история",
    "history.exportChooseRecords": "Выбрать записи",
    "history.pickerTruncated": "Показаны {n} самых новых записей. Более ранние записи за этот период тоже включены.",
    "history.exportIdentityTitle": "Официальное имя и дата рождения",
    "history.exportIdentityHelp": "Они нужны клинике в сводке. Можно сохранить их для следующего раза или использовать один раз. Если оставить имя пустым, в документе будет «Name not provided».",
    "history.exportLegalName": "Официальное имя",
    "history.exportDateOfBirth": "Дата рождения",
    "history.exportSaveIdentity": "Сохранить для следующих экспортов",
    "history.exportUseSaved": "Использовать сохранённые имя и дату рождения",
    "history.exportChangeIdentity": "Указать другие имя или дату рождения",
    "history.exportDownload": "Скачать",
    "history.exportWorking": "Готовим экспорт…",
    "history.exportDone": "Экспорт скачан",
    "history.exportError": "Не удалось экспортировать медицинскую историю",
    "history.exportTooLarge": "Экспорт слишком большой. Отключите документы или выберите меньше записей и повторите.",
    "history.exportIdentityInvalid": "Укажите настоящую дату рождения или оставьте поле пустым.",
    "history.share": "Поделиться",
    "history.shareTitle": "Поделиться с врачом",
    "history.shareDescription": "Ссылка на ограниченное время. Её можно отключить. Уже скачанные копии останутся у получателя.",
    "history.shareExpires": "Срок ссылки",
    "history.shareExpires.1h": "1 час",
    "history.shareExpires.24h": "24 часа",
    "history.shareExpires.7d": "7 дней",
    "history.shareExpires.30d": "30 дней",
    "history.shareRequireCode": "Нужен 6-значный код",
    "history.shareNotify": "Написать мне, когда ссылку откроют в первый раз",
    "history.shareConsentAccept": "Я соглашаюсь отправить эти записи",
    "history.shareCreate": "Создать ссылку",
    "history.shareWorking": "Создаём ссылку…",
    "history.shareCodeOnce": "Скопируйте ссылку и код сейчас. Показать их снова нельзя.",
    "history.shareUrl": "Ссылка",
    "history.shareCode": "Код доступа",
    "history.shareCopy": "Копировать ссылку",
    "history.shareCopyCode": "Копировать код",
    "history.shareCopied": "Ссылка скопирована",
    "history.shareCodeCopied": "Код скопирован",
    "history.shareDone": "Готово",
    "history.shareError": "Не удалось обновить доступ",
    "history.shareConsentRequired": "Подтвердите согласие на передачу, прежде чем создавать ссылку.",
    "history.shareLimit": "У этого человека уже максимум активных ссылок.",
    "history.shareWholeHistory": "Вся история",
    "history.shareChooseRecords": "Выбрать записи",
    "history.shareIncludeSummary": "Включить сводку",
    "history.sharePersonFallback": "этого человека",
    "history.sharesTitle": "Ссылки для врача",
    "history.noActiveShares": "Нет активных ссылок",
    "history.shareStop": "Прекратить доступ",
    "history.shareStopped": "Ссылка отключена",
    "history.shareOpens": "Открывали {count} раз",
    "history.shareExpiresAt": "Истекает {date}",
    "history.shareCreatedAt": "Создана {date}",
    "history.shareScopeSummary": "Сводка",
    "history.shareScopeRecords": "{count} записей",
    "history.shareScopeAi": "Включая мнения ИИ",
    "history.downloadPacket": "Скачать",
    "history.shareWithDoctor": "Поделиться с врачом",
    "history.packetTitle": "Подготовить для врача",
    "history.packetDescription": "Выберите, что увидит врач, затем скачайте файл или создайте ссылку.",
    "history.packetStepInclude": "Что включить",
    "history.packetStepSend": "Как отправить",
    "history.scopeProfile": "Сводка профиля (аллергии, лекарства, диагнозы, образ жизни)",
    "history.scopeIdentity": "Официальное имя и дата рождения",
    "history.scopeDocuments": "Приложить исходные документы",
    "history.shareDoctorNote": "Заметка для врача",
    "history.scopeNote": "Заметка для врача",
    "history.scopeNotePlaceholder": "Необязательно. До 1000 символов.",
    "history.scopePeriodWhole": "Период: вся история",
    "history.scopePreview": "{records} записей · {documents} документов ({size}) · {consultations} консультаций",
    "history.scopePreviewLoading": "Считаем записи…",
    "history.packetSaveIdentity": "Сохранено для следующих пакетов",
    "history.packetSaveRequired": "Ссылка с именем или датой рождения сохраняет их для следующих пакетов.",
    "history.packetIdentityOmitted": "В этой ссылке не будет имени и даты рождения.",
    "history.chooseOneItem": "Выберите хотя бы один пункт.",
    "history.profileAsOf": "Профиль актуален на {date}",
    "history.pregnancy": "Беременность",
    "history.activeProblems": "Активные проблемы",
    "history.vitals": "Показатели и образ жизни",
    "history.height": "Рост",
    "history.weight": "Вес",
    "history.bmi": "ИМТ",
    "history.smoking": "Курение",
    "history.alcohol": "Алкоголь",
    "history.activity": "Активность",
    "history.sleep": "Сон",
    "history.surgeries": "Процедуры и операции",
    "history.scopeError.empty": "Выберите хотя бы один пункт.",
    "history.scopeError.outside_period": "Одна или несколько записей вне выбранного периода.",
    "history.scopeError.not_found": "Одна или несколько записей не найдены.",
    "history.scopeError.invalid_period": "Такой период указать нельзя.",
    "history.scopeError.note_too_long": "Заметка для врача — не длиннее 1000 символов.",
    "history.scopeError.ambiguous_shape": "Записи можно выбрать только одним способом.",
    "history.shareScopeProfile": "профиль",
    "history.shareScopeIdentity": "личность",
    "history.shareScopeDocuments": "документы",
    "history.shareScopeAiShort": "ИИ",
    "history.shareScopePeriod.whole": "Вся история",
    "history.shareScopePeriod.12m": "Последние 12 месяцев",
    "history.shareScopePeriod.3y": "Последние 3 года",
    "history.shareScopePeriod.5y": "Последние 5 лет",
    "history.shareScopePeriod.custom": "Свой период",
    "history.shareScopePeriodRange": "{label} ({from} – {to})",
    "history.period.12m": "Последние 12 месяцев",
    "history.period.3y": "Последние 3 года",
    "history.period.5y": "Последние 5 лет",
    "history.period.all": "Вся история",
    "history.period.custom": "Свой период",
    "history.periodChip": "{label} ({count})",
    "history.periodFrom": "С",
    "history.periodTo": "По",
    "history.periodUndated": "Включить записи без даты ({count})",
    "history.periodInvalid": "Укажите дату начала не позже даты окончания.",
    "history.shareLog": "Журнал доступа",
    "history.shareLogEmpty": "Пока не открывали",
    "history.shareEvent.unlocked": "Открыто",
    "history.shareEvent.file_downloaded": "Файл скачан",
    "history.shareEvent.exported": "Экспорт",
    "history.shareClient.chrome": "Chrome",
    "history.shareClient.firefox": "Firefox",
    "history.shareClient.safari": "Safari",
    "history.shareClient.edge": "Edge",
    "history.shareClient.other": "Другой браузер",
    "history.shareBanner": "Передано пациентом или тем, кто о нём заботится. MedConcil это не проверял. Мнения ИИ помечены и не являются диагнозом.",
    "history.shareCodePrompt": "Введите код доступа",
    "history.shareCodeLabel": "Код доступа",
    "history.shareCodeContinue": "Продолжить",
    "history.shareCodeOptional": "Если код не сообщали, нажмите «Продолжить».",
    "history.shareInvalidCode": "Код не подходит.",
    "history.shareLocked": "Слишком много попыток. Попробуйте позже.",
    "history.shareNotFound": "Эта ссылка недоступна.",
    "history.shareDownloadPdf": "Скачать PDF",
    "history.shareDownloadFhir": "Скачать FHIR",
    "history.shareDownloadZip": "Скачать ZIP",
    "history.shareLoading": "Загрузка…",
    "history.shareViewFile": "Документ",
    "history.shareCloseFile": "Закрыть",

    // Patient form placeholders
    "patients.dosePlaceholder": "100 мг",
    "patients.freqPlaceholder": "2 р/д",
  },

  es: esLocale,
  ar: arLocale,
  fr: frLocale,
};

const BROWSER_LOCALE_MAP = {
  en: "en",
  es: "es",
  ar: "ar",
  fr: "fr",
  ru: "ru",
};

// Detect user's preferred language
export const detectLanguage = () => {
  const region = process.env.REACT_APP_REGION || "cis";
  const allowed = region === "us" ? ["en", "es", "ar", "fr"] : ["ru"];
  const defaultLang = region === "us" ? "en" : "ru";

  const saved = localStorage.getItem("council_language");
  if (saved && allowed.includes(saved)) {
    return saved;
  }
  if (saved === "ru" && region === "us") {
    localStorage.setItem("council_language", defaultLang);
    return defaultLang;
  }

  const browserLang = (navigator.language || navigator.userLanguage || "").toLowerCase();
  const prefix = browserLang.split("-")[0];
  const mapped = BROWSER_LOCALE_MAP[prefix];
  if (mapped && allowed.includes(mapped)) {
    return mapped;
  }

  return defaultLang;
};

export const setLanguage = (lang) => {
  localStorage.setItem('council_language', lang);
};
